import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Check,
  Search,
  X,
  Edit3,
  Shirt,
  Sparkles,
  Layers,
  Wind,
  Crown,
  Briefcase,
  CircleDot,
  Footprints,
  Heart,
  Gem,
  MousePointerClick,
  Maximize2,
  Minimize2,
  Upload,
  Loader2,
} from 'lucide-react';
import { uploadImageWithFallback, compressImage } from '../utils/s3Upload';
import { getWomenGarmentTypeIcon } from '../data/womenGarmentTypeIcons';
import { getMenGarmentTypeIcon } from '../data/menGarmentTypeIcons';
import {
  getGarmentInspirationImage,
  hasGarmentInspirationCategory,
  KIDS_BOY_TODDLER_PREVIEW,
  KIDS_GIRL_TODDLER_PREVIEW,
} from '../data/garmentInspirationImages';
import {
  buildGarmentInspirationImageUrl,
  hasCustomizationSelections,
} from '../utils/garmentInspirationRef';
import { PRODUCT_CATEGORIES_OPTIMIZED } from '../data/productCategoriesOptimized';
import {
  kidsBoyUsesMensCatalog,
  kidsGirlUsesWomensCatalog,
  resolveKidsCatalogCategory,
  isMensCatalogContext,
  isWomensCatalogContext,
  filterKidsGirlProductTypes,
} from '../utils/kidsProductCatalog';
import {
  MENS_TSHIRT_ASSET_MAP,
  MENS_KURTA_ASSET_MAP,
  MENS_DHOTI_ASSET_MAP,
  MENS_PAJAMA_ASSET_MAP,
  MENS_CORDSET_ASSET_MAP,
  MENS_PANTS_ASSET_MAP,
  MENS_JACKET_ASSET_MAP,
  MENS_SHIRT_ASSET_MAP,
  MENS_BLAZER_ASSET_MAP,
  MENS_WAISTCOAT_ASSET_MAP,
  KIDS_BOY_TODDLER_ASSET_MAP,
  WOMEN_TOP_ASSET_MAP,
  WOMEN_DRESS_ASSET_MAP,
  WOMEN_TRADITIONAL_BOTTOMS_ASSET_MAP,
  WOMEN_BLOUSES_ASSET_MAP,
  WOMEN_SAREE_STYLES_ASSET_MAP,
  WOMEN_LEHENGA_ASSET_MAP,
  WOMEN_NIGHT_WEAR_ASSET_MAP,
  WOMEN_LINGERIE_ASSET_MAP,
  WOMEN_BLAZER_ASSET_MAP,
  WOMEN_HOODIES_ASSET_MAP,
  WOMEN_LAYERING_ASSET_MAP,
  WOMEN_JACKET_ASSET_MAP,
  WOMEN_PANT_ASSET_MAP,
  WOMEN_SKIRT_ASSET_MAP,
  WOMEN_SHIRT_ASSET_MAP,
  SOCKS_ASSET_MAP,
} from '../data/customizationAssetMaps.generated';


const DEFAULT_VISUAL = { Icon: Shirt, bg: 'from-indigo-500 to-violet-600' };

const GARMENT_TYPE_TILE_BG = 'from-white to-slate-50';

function GarmentTypeTileIcon({ tile, Icon }) {
  if (tile?.img) {
    return (
      <img
        src={encodeAssetPath(tile.img)}
        alt=""
        className="h-8 w-8 object-contain sm:h-9 sm:w-9"
        draggable={false}
      />
    );
  }
  if (tile?.emoji) {
    return (
      <span className="text-[22px] leading-none" aria-hidden="true">
        {tile.emoji}
      </span>
    );
  }
  return <Icon className="h-5 w-5 text-white" strokeWidth={2} />;
}

// Customisation data + icon system (ported from the provided `Product Types Modal.html`)
const ATTRIBUTES = {
  dress: [
    { key: 'neckline', label: 'Neckline', opts: ['Round', 'V-Neck', 'Square', 'Sweetheart', 'Off-Shoulder', 'Halter', 'Boat', 'Cowl', 'Asymmetric'] },
    { key: 'sleeve', label: 'Sleeve', opts: ['Sleeveless', 'Cap', 'Short', '3/4', 'Long', 'Puff', 'Bishop', 'Flutter', 'Bell'] },
    { key: 'length', label: 'Length', opts: ['Mini', 'Midi', 'Maxi', 'Knee-Length', 'Tea-Length', 'Floor-Length'] },
    { key: 'silhouette', label: 'Silhouette', opts: ['A-Line', 'Bodycon', 'Fit & Flare', 'Shift', 'Ballgown', 'Empire', 'Sheath', 'Peplum'] },
    { key: 'back', label: 'Back Style', opts: ['Regular Back', 'Open Back', 'Zippered', 'Tie Back', 'Keyhole'] },
  ],
  frock: [
    { key: 'neckline', label: 'Neckline', opts: ['Round', 'V-Neck', 'Square', 'Sweetheart', 'Collar', 'Peter Pan', 'Smocked'] },
    { key: 'sleeve', label: 'Sleeve', opts: ['Sleeveless', 'Short', 'Puff', '3/4', 'Long', 'Flutter'] },
    { key: 'length', label: 'Length', opts: ['Mini', 'Midi', 'Maxi', 'Knee-Length', 'Floor-Length'] },
    { key: 'waist', label: 'Waist Style', opts: ['Smocked Waist', 'Belted', 'Empire Waist', 'Natural', 'Dropped'] },
    { key: 'skirt', label: 'Skirt Type', opts: ['Pleated', 'Flared', 'Layered', 'Tiered', 'Straight'] },
  ],
  top: [
    { key: 'neckline', label: 'Neckline', opts: ['Round', 'V-Neck', 'Square', 'Scoop', 'Off-Shoulder', 'Halter', 'Wrap', 'Boat', 'Asymmetric'] },
    { key: 'sleeve', label: 'Sleeve', opts: ['Sleeveless', 'Cap', 'Short', '3/4', 'Long', 'Puff', 'Bell', 'Raglan'] },
    { key: 'fit', label: 'Fit', opts: ['Cropped', 'Regular', 'Oversized', 'Fitted', 'Boxy', 'Relaxed'] },
    { key: 'hem', label: 'Hem Style', opts: ['Straight Hem', 'Curved Hem', 'Tie-Front', 'Ruffled', 'High-Low'] },
    { key: 'closure', label: 'Closure', opts: ['Zip', 'Button', 'Wrap-front', 'Lace-Up'] },
  ],
  skirt: [
    { key: 'length', label: 'Length', opts: ['Micro', 'Mini', 'Midi', 'Maxi', 'Knee-Length', 'Floor-Length'] },
    { key: 'silhouette', label: 'Silhouette', opts: ['A-Line', 'Pencil', 'Pleated', 'Flared', 'Tiered', 'Circle', 'Tulip', 'Straight'] },
    { key: 'waist', label: 'Waist Style', opts: ['High-Waist', 'Mid-Waist', 'Low-Rise', 'Paperbag', 'Elasticated', 'Belted'] },
    { key: 'slit', label: 'Slit', opts: ['None', 'Front', 'Side', 'Back Slit', 'Double'] },
    { key: 'closure', label: 'Closure', opts: ['Zip', 'Button', 'Wrap-front', 'Elasticated', 'Hook & Eye'] },
  ],
  tshirt: [
    { key: 'neckline', label: 'Neckline', opts: ['Round', 'V-Neck', 'Polo', 'Henley', 'Scoop', 'Turtleneck'] },
    { key: 'sleeve', label: 'Sleeve', opts: ['Sleeveless', 'Short', '3/4', 'Long', 'Raglan', 'Drop-Shoulder'] },
    { key: 'fit', label: 'Fit', opts: ['Regular', 'Slim', 'Oversized', 'Cropped', 'Boxy'] },
    { key: 'hem', label: 'Hem', opts: ['Straight Hem', 'Curved Hem', 'Ruffled', 'Tied'] },
  ],
  jeans: [
    { key: 'fit', label: 'Fit', opts: ['Skinny', 'Slim', 'Straight', 'Bootcut', 'Flared', 'Relaxed', 'Boyfriend', 'Mom', 'Wide-Leg'] },
    { key: 'rise', label: 'Rise', opts: ['High-Rise', 'Mid-Rise', 'Low-Rise'] },
    { key: 'length', label: 'Length', opts: ['Full-Length', 'Cropped', 'Ankle', 'Bermuda', 'Capri', 'Shorts'] },
    { key: 'waist', label: 'Waist', opts: ['Elasticated', 'Zip & Button', 'Paperbag', 'Belted'] },
    { key: 'distress', label: 'Distress', opts: ['None', 'Light', 'Medium', 'Heavy', 'Ripped'] },
  ],
  shorts: [
    { key: 'length', label: 'Length', opts: ['Micro', 'Mini', 'Bermuda', 'Knee-Length', 'Capri'] },
    { key: 'fit', label: 'Fit', opts: ['Slim', 'Regular', 'Relaxed', 'Oversized', 'Biker'] },
    { key: 'waist', label: 'Waist', opts: ['High-Waist', 'Mid-Rise', 'Low-Rise', 'Elasticated', 'Paperbag', 'Belted'] },
    { key: 'closure', label: 'Closure', opts: ['Zip', 'Button', 'Drawstring', 'Elasticated'] },
  ],
  sweater: [
    { key: 'neckline', label: 'Neckline', opts: ['Crew Neck', 'V-Neck', 'Turtleneck', 'Cowl', 'Mock Neck', 'Boat', 'Off-Shoulder'] },
    { key: 'sleeve', label: 'Sleeve', opts: ['Long', '3/4', 'Short', 'Sleeveless', 'Puff', 'Bell'] },
    { key: 'fit', label: 'Fit', opts: ['Regular', 'Oversized', 'Fitted', 'Cropped', 'Boxy'] },
    { key: 'length', label: 'Length', opts: ['Cropped', 'Hip-Length', 'Tunic', 'Longline'] },
    { key: 'knit', label: 'Knit Style', opts: ['Cable', 'Ribbed', 'Chunky', 'Fine', 'Jacquard', 'Open Knit'] },
  ],
  jacket: [
    { key: 'collar', label: 'Collar', opts: ['Lapel', 'Notch', 'Peak', 'Shawl', 'Hood', 'Stand', 'Mock Neck'] },
    { key: 'closure', label: 'Closure', opts: ['Single-Breasted', 'Double-Breasted', 'Zip', 'Button', 'Snap', 'Belted Closure'] },
    { key: 'length', label: 'Length', opts: ['Cropped', 'Hip-Length', 'Longline', 'Knee-Length', 'Thigh'] },
    { key: 'pocket', label: 'Pockets', opts: ['None', 'Flap', 'Patch', 'Welt', 'Cargo', 'Hidden'] },
    { key: 'sleeve', label: 'Sleeve', opts: ['Regular', 'Raglan', 'Set-In', 'Drop-Shoulder'] },
  ],
};

function getMimeTypeFromAssetPath(p) {
  const s = String(p || '').split('?')[0].toLowerCase();
  if (s.endsWith('.png')) return 'image/png';
  if (s.endsWith('.jpg') || s.endsWith('.jpeg')) return 'image/jpeg';
  if (s.endsWith('.webp')) return 'image/webp';
  return 'application/octet-stream';
}

function toAbsoluteUrl(u) {
  if (!u) return u;
  const s = String(u);
  if (/^(https?:)?\/\//i.test(s)) return s;
  if (s.startsWith('data:') || s.startsWith('blob:')) return s;
  if (s.startsWith('/') && typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${s}`;
  }
  return s;
}

function getMensReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'men-clothing') return null;
  if (typeValue === 'tshirt') {
    return MENS_TSHIRT_ASSET_MAP?.[attrKey]?.[opt] || null;
  }
  if (typeValue === 'kurta') {
    return MENS_KURTA_ASSET_MAP?.[attrKey]?.[opt] || null;
  }
  if (typeValue === 'kurta-dhoti') {
    return MENS_DHOTI_ASSET_MAP?.[attrKey]?.[opt] || null;
  }
  if (typeValue === 'pajama') {
    return MENS_PAJAMA_ASSET_MAP?.[attrKey]?.[opt] || null;
  }
  if (typeValue === 'trouser') {
    return MENS_PANTS_ASSET_MAP?.[attrKey]?.[opt] || null;
  }
  if (typeValue === 'shirt') {
    return MENS_SHIRT_ASSET_MAP?.[attrKey]?.[opt] || null;
  }
  if (typeValue === 'jacket') {
    return MENS_JACKET_ASSET_MAP?.[attrKey]?.[opt] || null;
  }
  if (typeValue === 'blazer') {
    return MENS_BLAZER_ASSET_MAP?.[attrKey]?.[opt] || null;
  }
  if (typeValue === 'waistcoat') {
    return MENS_WAISTCOAT_ASSET_MAP?.[attrKey]?.[opt] || null;
  }
  if (typeValue === 'cordset') {
    return MENS_CORDSET_ASSET_MAP?.[attrKey]?.[opt] || null;
  }
  return null;
}

function getKidsToddlerReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'kids-boy' && categoryValue !== 'kids-girl') return null;
  if (typeValue !== 'toddler-dress') return null;
  if (attrKey !== 'style') return null;
  return KIDS_BOY_TODDLER_ASSET_MAP?.style?.[opt] || null;
}

function encodeAssetPath(p) {
  if (typeof p !== 'string' || !p) return p;
  const qIdx = p.indexOf('?');
  const pathPart = qIdx >= 0 ? p.slice(0, qIdx) : p;
  const query = qIdx >= 0 ? p.slice(qIdx) : '';
  if (!pathPart.startsWith('/')) return encodeURI(p);
  const encoded = pathPart
    .split('/')
    .map((segment) => (segment ? encodeURIComponent(segment) : segment))
    .join('/');
  return encoded + query;
}

function getWomenTopReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'women-clothing') return null;
  if (typeValue !== 'top') return null;
  return encodeAssetPath(WOMEN_TOP_ASSET_MAP?.[attrKey]?.[opt] || null);
}

function getWomenPantReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'women-clothing') return null;
  // Women "Pant" is the women type with value "jeans" (display label: Pant).
  if (typeValue !== 'jeans') return null;
  return encodeAssetPath(WOMEN_PANT_ASSET_MAP?.[attrKey]?.[opt] || null);
}

function getWomenSkirtReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'women-clothing') return null;
  if (typeValue !== 'skirt') return null;
  return encodeAssetPath(WOMEN_SKIRT_ASSET_MAP?.[attrKey]?.[opt] || null);
}

function getWomenDressReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'women-clothing') return null;
  if (typeValue !== 'dress') return null;
  return encodeAssetPath(WOMEN_DRESS_ASSET_MAP?.[attrKey]?.[opt] || null);
}

function getWomenTraditionalBottomsReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'women-clothing') return null;
  if (typeValue !== 'trouser') return null;
  return encodeAssetPath(WOMEN_TRADITIONAL_BOTTOMS_ASSET_MAP?.[attrKey]?.[opt] || null);
}

function getWomenLehengaReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'women-clothing') return null;
  if (typeValue !== 'lehenga') return null;
  return encodeAssetPath(WOMEN_LEHENGA_ASSET_MAP?.[attrKey]?.[opt] || null);
}

function getWomenBlousesReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'women-clothing') return null;
  if (typeValue !== 'blouses') return null;
  return encodeAssetPath(WOMEN_BLOUSES_ASSET_MAP?.[attrKey]?.[opt] || null);
}

function getWomenSareeStylesReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'women-clothing') return null;
  if (typeValue !== 'saree-styles') return null;
  return encodeAssetPath(WOMEN_SAREE_STYLES_ASSET_MAP?.[attrKey]?.[opt] || null);
}

function getWomenNightWearReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'women-clothing') return null;
  if (typeValue !== 'night-wear') return null;
  return encodeAssetPath(WOMEN_NIGHT_WEAR_ASSET_MAP?.[attrKey]?.[opt] || null);
}

function getWomenLingerieReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'women-clothing') return null;
  if (typeValue !== 'lingerie') return null;
  return encodeAssetPath(WOMEN_LINGERIE_ASSET_MAP?.[attrKey]?.[opt] || null);
}

function getWomenBlazerReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'women-clothing') return null;
  if (typeValue !== 'blazer') return null;
  return encodeAssetPath(WOMEN_BLAZER_ASSET_MAP?.[attrKey]?.[opt] || null);
}

function getWomenHoodiesReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'women-clothing') return null;
  if (typeValue !== 'hoodies') return null;
  return encodeAssetPath(WOMEN_HOODIES_ASSET_MAP?.[attrKey]?.[opt] || null);
}

function getWomenLayeringReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'women-clothing') return null;
  if (typeValue !== 'layering') return null;
  return encodeAssetPath(WOMEN_LAYERING_ASSET_MAP?.[attrKey]?.[opt] || null);
}

function getWomenJacketReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'women-clothing') return null;
  if (typeValue !== 'jacket') return null;
  return encodeAssetPath(WOMEN_JACKET_ASSET_MAP?.[attrKey]?.[opt] || null);
}

function isWomenShirtType(typeValue) {
  const v = String(typeValue || '').toLowerCase();
  return v === 'shirt' || v.startsWith('shirt-');
}

function getWomenShirtReferenceAsset(categoryValue, typeValue, attrKey, opt) {
  if (categoryValue !== 'women-clothing') return null;
  if (!isWomenShirtType(typeValue)) return null;
  return encodeAssetPath(WOMEN_SHIRT_ASSET_MAP?.[attrKey]?.[opt] || null);
}

function getSocksReferenceAsset(typeValue, attrKey, opt) {
  if (typeValue !== 'socks') return null;
  return encodeAssetPath(SOCKS_ASSET_MAP?.[attrKey]?.[opt] || null);
}

export function getReferenceAsset({ categoryValue, typeValue, attrKey, opt, kidAgeRange }) {
  const catalogCategory = resolveKidsCatalogCategory(categoryValue, kidAgeRange);
  const socks = getSocksReferenceAsset(typeValue, attrKey, opt);
  if (socks) return socks;
  const mens = getMensReferenceAsset(catalogCategory, typeValue, attrKey, opt);
  if (mens) return encodeAssetPath(mens);
  // Toddler-dress should always resolve to toddler assets (even if caller didn't pass kidAgeRange).
  if (kidAgeRange === 'toddlers' || typeValue === 'toddler-dress') {
    const toddler = getKidsToddlerReferenceAsset(categoryValue, typeValue, attrKey, opt);
    if (toddler) return encodeAssetPath(toddler);
  }
  const womenTop = getWomenTopReferenceAsset(catalogCategory, typeValue, attrKey, opt);
  if (womenTop) return womenTop;
  const womenPant = getWomenPantReferenceAsset(catalogCategory, typeValue, attrKey, opt);
  if (womenPant) return womenPant;
  const womenSkirt = getWomenSkirtReferenceAsset(catalogCategory, typeValue, attrKey, opt);
  if (womenSkirt) return womenSkirt;
  const womenDress = getWomenDressReferenceAsset(catalogCategory, typeValue, attrKey, opt);
  if (womenDress) return womenDress;
  const womenTraditionalBottoms = getWomenTraditionalBottomsReferenceAsset(catalogCategory, typeValue, attrKey, opt);
  if (womenTraditionalBottoms) return womenTraditionalBottoms;
  const womenLehenga = getWomenLehengaReferenceAsset(catalogCategory, typeValue, attrKey, opt);
  if (womenLehenga) return womenLehenga;
  const womenBlouses = getWomenBlousesReferenceAsset(catalogCategory, typeValue, attrKey, opt);
  if (womenBlouses) return womenBlouses;
  const womenSareeStyles = getWomenSareeStylesReferenceAsset(catalogCategory, typeValue, attrKey, opt);
  if (womenSareeStyles) return womenSareeStyles;
  const womenNightWear = getWomenNightWearReferenceAsset(catalogCategory, typeValue, attrKey, opt);
  if (womenNightWear) return womenNightWear;
  const womenLingerie = getWomenLingerieReferenceAsset(catalogCategory, typeValue, attrKey, opt);
  if (womenLingerie) return womenLingerie;
  const womenBlazer = getWomenBlazerReferenceAsset(catalogCategory, typeValue, attrKey, opt);
  if (womenBlazer) return womenBlazer;
  const womenHoodies = getWomenHoodiesReferenceAsset(catalogCategory, typeValue, attrKey, opt);
  if (womenHoodies) return womenHoodies;
  const womenLayering = getWomenLayeringReferenceAsset(catalogCategory, typeValue, attrKey, opt);
  if (womenLayering) return womenLayering;
  const womenJacket = getWomenJacketReferenceAsset(catalogCategory, typeValue, attrKey, opt);
  if (womenJacket) return womenJacket;
  const womenShirt = getWomenShirtReferenceAsset(catalogCategory, typeValue, attrKey, opt);
  if (womenShirt) return womenShirt;
  return null;
}

const Icon = ({ d, d2, children, vb = '0 0 40 52', className = 'h-10 w-10' }) => (
  <svg
    viewBox={vb}
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
    {d && <path d={d} />}
    {d2 && <path d={d2} />}
  </svg>
);

// -- Neckline icons (subset used by ATTRIBUTES above)
const NeckRound = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M10 6 Q20 18 30 6"/></Icon>;
const NeckV = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M10 6 L20 22 L30 6"/></Icon>;
const NeckSquare = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M10 6 L10 16 L30 16 L30 6"/></Icon>;
const NeckSweetheart = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M10 6 Q14 4 17 10 Q20 16 23 10 Q26 4 30 6"/></Icon>;
const NeckOffShoulder = () => <Icon><path d="M4 10 L4 46 L36 46 L36 10"/><path d="M4 10 Q20 16 36 10"/><path d="M4 10 L8 4"/><path d="M36 10 L32 4"/></Icon>;
const NeckHalter = () => <Icon><path d="M12 20 L6 46 L34 46 L28 20"/><path d="M12 20 Q16 14 20 6 Q24 14 28 20"/></Icon>;
const NeckBoat = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M10 6 Q20 12 30 6"/></Icon>;
const NeckCowl = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M10 6 Q13 12 20 14 Q27 12 30 6"/><path d="M12 10 Q15 16 20 18 Q25 16 28 10" strokeDasharray="2 2"/></Icon>;
const NeckAsymmetric = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M10 6 L10 4 L30 14"/></Icon>;
const NeckCollar = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M10 6 L14 12 L20 10 L26 12 L30 6"/></Icon>;
const NeckPeterPan = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M10 6 Q16 4 20 10 Q24 4 30 6"/><path d="M10 10 Q16 8 20 14 Q24 8 30 10" strokeDasharray="2 2"/></Icon>;
const NeckSmocked = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M10 8 Q20 12 30 8"/><path d="M10 12 Q20 16 30 12" strokeDasharray="2 1"/></Icon>;
const NeckScoop = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M10 6 Q20 22 30 6"/></Icon>;
const NeckWrap = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M10 6 L22 22 L30 6"/><path d="M30 10 L18 26"/></Icon>;
const NeckTurtleneck = () => <Icon><path d="M10 10 L6 46 L34 46 L30 10"/><rect x="13" y="2" width="14" height="10" rx="5"/></Icon>;
const NeckPolo = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M16 6 L16 16 L24 16 L24 6"/><path d="M16 6 Q20 10 24 6"/></Icon>;
const NeckHenley = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M17 6 L17 20"/><path d="M23 6 L23 20"/><path d="M10 6 Q20 14 30 6"/></Icon>;
const NeckCrewNeck = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M10 6 Q20 16 30 6"/><circle cx="20" cy="12" r="5"/></Icon>;
const NeckMockNeck = () => <Icon><path d="M10 8 L6 46 L34 46 L30 8"/><rect x="13" y="2" width="14" height="8" rx="3"/></Icon>;
const NeckNotch = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M10 6 L16 14 L20 10 L24 14 L30 6"/></Icon>;
const NeckPeak = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M10 6 L14 18 L20 12 L26 18 L30 6"/></Icon>;
const NeckShawl = () => <Icon><path d="M10 6 L6 46 L34 46 L30 6"/><path d="M10 6 L14 18 L20 14 L26 18 L30 6"/></Icon>;
const NeckHood = () => <Icon><path d="M10 10 L6 46 L34 46 L30 10"/><path d="M10 10 Q10 0 20 0 Q30 0 30 10"/><path d="M13 8 Q20 4 27 8"/></Icon>;
const NeckStand = () => <Icon><path d="M10 8 L6 46 L34 46 L30 8"/><path d="M12 2 L12 10 L28 10 L28 2 L12 2"/></Icon>;

// -- Sleeve icons
const SleeveSleeveless = () => <Icon><path d="M12 4 L8 46 L32 46 L28 4"/><path d="M12 4 Q20 12 28 4"/></Icon>;
const SleeveCap = () => <Icon><path d="M12 8 L8 46 L32 46 L28 8"/><path d="M12 8 Q20 14 28 8"/><path d="M12 8 Q8 4 10 10"/><path d="M28 8 Q32 4 30 10"/></Icon>;
const SleeveShort = () => <Icon><path d="M12 6 L8 46 L32 46 L28 6"/><path d="M12 6 Q20 14 28 6"/><path d="M12 6 L6 18 L14 20"/><path d="M28 6 L34 18 L26 20"/></Icon>;
const SleeveTQ = () => <Icon><path d="M12 6 L8 46 L32 46 L28 6"/><path d="M12 6 Q20 14 28 6"/><path d="M12 6 L5 28 L13 30"/><path d="M28 6 L35 28 L27 30"/></Icon>;
const SleeveLong = () => <Icon><path d="M12 6 L8 46 L32 46 L28 6"/><path d="M12 6 Q20 14 28 6"/><path d="M12 6 L5 42 L13 44"/><path d="M28 6 L35 42 L27 44"/></Icon>;
const SleevePuff = () => <Icon><path d="M12 8 L8 46 L32 46 L28 8"/><path d="M12 8 Q20 16 28 8"/><path d="M12 8 Q4 2 4 14 Q4 20 12 18"/><path d="M28 8 Q36 2 36 14 Q36 20 28 18"/></Icon>;
const SleeveBishop = () => <Icon><path d="M12 6 L8 46 L32 46 L28 6"/><path d="M12 6 Q20 14 28 6"/><path d="M12 6 Q8 16 10 42 L14 44"/><path d="M28 6 Q32 16 30 42 L26 44"/></Icon>;
const SleeveFlutter = () => <Icon><path d="M12 6 L8 46 L32 46 L28 6"/><path d="M12 6 Q20 14 28 6"/><path d="M12 6 Q6 8 4 16 Q8 14 12 18"/><path d="M28 6 Q34 8 36 16 Q32 14 28 18"/></Icon>;
const SleeveBell = () => <Icon><path d="M12 6 L8 46 L32 46 L28 6"/><path d="M12 6 Q20 14 28 6"/><path d="M12 6 Q10 18 4 42 L14 44"/><path d="M28 6 Q30 18 36 42 L26 44"/></Icon>;
const SleeveRaglan = () => <Icon><path d="M14 10 L8 46 L32 46 L26 10"/><path d="M4 4 L14 20"/><path d="M36 4 L26 20"/><path d="M4 4 L14 10 L26 10 L36 4"/></Icon>;
const SleeveDropShoulder = () => <Icon><path d="M14 12 L8 46 L32 46 L26 12"/><path d="M8 12 L14 22"/><path d="M32 12 L26 22"/><path d="M8 12 Q20 18 32 12"/></Icon>;
const SleeveSetIn = () => <Icon><path d="M12 8 L8 46 L32 46 L28 8"/><path d="M8 8 Q4 4 5 14 L12 14"/><path d="M32 8 Q36 4 35 14 L28 14"/><path d="M12 8 Q20 14 28 8"/></Icon>;

// -- Length icons
const LengthMicro = () => <Icon><path d="M10 4 L6 22 L34 22 L30 4 Q20 12 10 4"/></Icon>;
const LengthMini = () => <Icon><path d="M10 4 L6 28 L34 28 L30 4 Q20 12 10 4"/></Icon>;
const LengthMidi = () => <Icon><path d="M10 4 L5 36 L35 36 L30 4 Q20 12 10 4"/></Icon>;
const LengthMaxi = () => <Icon><path d="M10 4 L4 48 L36 48 L30 4 Q20 12 10 4"/></Icon>;
const LengthKnee = () => <Icon><path d="M10 4 L6 32 L34 32 L30 4 Q20 12 10 4"/><line x1="6" y1="32" x2="34" y2="32" strokeDasharray="3 2"/></Icon>;
const LengthTea = () => <Icon><path d="M10 4 L5 40 L35 40 L30 4 Q20 12 10 4"/><line x1="5" y1="40" x2="35" y2="40" strokeDasharray="3 2"/></Icon>;
const LengthFloor = () => <Icon><path d="M10 4 L4 50 L36 50 L30 4 Q20 12 10 4"/></Icon>;
const LengthFull = () => <Icon><path d="M12 4 L6 50 L34 50 L28 4 Q20 10 12 4"/></Icon>;
const LengthCropped = () => <Icon><path d="M10 4 L8 24 L32 24 L30 4 Q20 12 10 4"/></Icon>;
const LengthAnkle = () => <Icon><path d="M12 4 L8 46 L32 46 L28 4"/><path d="M12 4 Q20 12 28 4"/></Icon>;
const LengthCapri = () => <Icon><path d="M12 4 L9 36 L31 36 L28 4"/><path d="M12 4 Q20 12 28 4"/></Icon>;
const LengthShorts = () => <Icon><path d="M12 4 L10 26 L30 26 L28 4"/><path d="M12 4 Q20 12 28 4"/><line x1="20" y1="4" x2="20" y2="26"/></Icon>;
const LengthBermuda = () => <Icon><path d="M12 4 L10 30 L30 30 L28 4"/><path d="M12 4 Q20 12 28 4"/><line x1="20" y1="4" x2="20" y2="30"/></Icon>;
const LengthHip = () => <Icon><path d="M12 4 L8 28 L32 28 L28 4 Q20 10 12 4"/></Icon>;
const LengthLongline = () => <Icon><path d="M12 4 L6 44 L34 44 L28 4 Q20 10 12 4"/></Icon>;
const LengthTunic = () => <Icon><path d="M12 4 L8 36 L32 36 L28 4 Q20 10 12 4"/></Icon>;

// -- Silhouette icons
const SilALine = () => <Icon><path d="M16 4 L6 48 L34 48 L24 4 Q20 8 16 4"/></Icon>;
const SilBodycon = () => <Icon><path d="M14 4 L12 48 L28 48 L26 4 Q20 8 14 4"/></Icon>;
const SilFitFlare = () => <Icon><path d="M14 4 L12 26 Q10 30 4 48 L36 48 Q30 30 28 26 L26 4 Q20 8 14 4"/></Icon>;
const SilShift = () => <Icon><path d="M13 4 L10 48 L30 48 L27 4 Q20 8 13 4"/></Icon>;
const SilBallgown = () => <Icon><path d="M16 4 L14 22 Q4 28 2 50 L38 50 Q36 28 26 22 L24 4 Q20 8 16 4"/></Icon>;
const SilEmpire = () => <Icon><path d="M14 4 L12 16 Q6 22 4 48 L36 48 Q34 22 28 16 L26 4 Q20 8 14 4"/><line x1="10" y1="16" x2="30" y2="16"/></Icon>;
const SilSheath = () => <Icon><path d="M15 4 L12 48 L28 48 L25 4 Q20 8 15 4"/><line x1="12" y1="30" x2="28" y2="30" strokeDasharray="2 2"/></Icon>;
const SilPeplum = () => <Icon><path d="M14 4 L12 28 Q6 30 4 36 L36 36 Q34 30 28 28 L26 4 Q20 8 14 4"/><path d="M12 36 L8 48 L32 48 L28 36"/></Icon>;
const SilPleated = () => <Icon><path d="M14 4 L8 48 L32 48 L26 4 Q20 10 14 4"/><line x1="14" y1="20" x2="10" y2="48"/><line x1="17" y1="18" x2="14" y2="48"/><line x1="20" y1="17" x2="18" y2="48"/><line x1="23" y1="18" x2="22" y2="48"/><line x1="26" y1="20" x2="26" y2="48"/></Icon>;
const SilFlared = () => <Icon><path d="M15 4 L10 30 Q4 34 2 48 L38 48 Q36 34 30 30 L25 4 Q20 8 15 4"/></Icon>;
const SilLayered = () => <Icon><path d="M14 4 L8 48 L32 48 L26 4 Q20 10 14 4"/><path d="M9 28 Q20 32 31 28" strokeDasharray="3 2"/><path d="M8 38 Q20 42 32 38" strokeDasharray="3 2"/></Icon>;
const SilTiered = () => <Icon><path d="M16 4 Q20 10 24 4 L28 22 Q20 24 12 22 Z"/><path d="M10 22 Q20 26 30 22 L34 40 Q20 44 6 40 Z"/><path d="M6 40 Q20 46 34 40 L36 50 L4 50 Z"/></Icon>;
const SilCircle = () => <Icon><path d="M16 4 Q20 10 24 4 L30 48 L10 48 Z"/><path d="M16 4 Q10 26 10 48"/><path d="M24 4 Q30 26 30 48"/></Icon>;
const SilTulip = () => <Icon><path d="M15 4 L12 28 Q8 32 8 44 L20 44 L20 28 L20 44 L32 44 Q32 32 28 28 L25 4 Q20 8 15 4"/></Icon>;
const SilStraight = () => <Icon><path d="M13 4 L11 48 L29 48 L27 4 Q20 8 13 4"/></Icon>;
const SilPencil = () => <Icon><path d="M14 4 L12 44 Q14 48 18 48 L22 48 Q26 48 28 44 L26 4 Q20 8 14 4"/></Icon>;

// -- Rise / Waist icons
const RiseHigh = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4"/><path d="M12 4 Q20 8 28 4"/><line x1="10" y1="14" x2="30" y2="14" strokeWidth="2.5" stroke="currentColor"/></Icon>;
const RiseMid = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4"/><path d="M12 4 Q20 8 28 4"/><line x1="9" y1="22" x2="31" y2="22" strokeWidth="2.5" stroke="currentColor"/></Icon>;
const RiseLow = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4"/><path d="M12 4 Q20 8 28 4"/><line x1="9" y1="32" x2="31" y2="32" strokeWidth="2.5" stroke="currentColor"/></Icon>;
const WaistPaperbag = () => <Icon><path d="M10 14 L8 48 L32 48 L30 14"/><path d="M10 14 Q12 8 20 10 Q28 8 30 14"/><path d="M12 8 L12 14 M16 6 L16 14 M20 6 L20 14 M24 6 L24 14 M28 8 L28 14"/></Icon>;
const WaistElastic = () => <Icon><path d="M10 12 L8 48 L32 48 L30 12"/><rect x="9" y="6" width="22" height="8" rx="2"/><line x1="11" y1="9" x2="29" y2="9" strokeDasharray="2 2"/></Icon>;
const WaistBelted = () => <Icon><path d="M10 12 L8 48 L32 48 L30 12"/><rect x="8" y="8" width="24" height="7" rx="2"/><circle cx="20" cy="11.5" r="2"/></Icon>;
const WaistSmocked = () => <Icon><path d="M10 14 L8 48 L32 48 L30 14"/><path d="M10 8 Q15 6 20 8 Q25 10 30 8"/><path d="M10 11 Q15 9 20 11 Q25 13 30 11"/><path d="M10 14 Q15 12 20 14 Q25 16 30 14"/></Icon>;
const WaistNatural = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4"/><path d="M12 4 Q20 8 28 4"/><path d="M10 20 Q20 22 30 20"/></Icon>;
const WaistDropped = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4"/><path d="M12 4 Q20 8 28 4"/><path d="M9 30 Q20 32 31 30"/></Icon>;
const WaistEmpire = () => <Icon><path d="M12 4 L6 48 L34 48 L28 4"/><path d="M12 4 Q20 8 28 4"/><line x1="10" y1="14" x2="30" y2="14" strokeWidth="2"/></Icon>;
const WaistZipBtn = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4"/><path d="M12 4 Q20 8 28 4"/><path d="M19 10 L19 48 M21 10 L21 48"/><circle cx="20" cy="12" r="2.5"/></Icon>;

// -- Distress icons
const DistressNone = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4"/><path d="M12 4 Q20 8 28 4"/></Icon>;
const DistressLight = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4"/><path d="M12 4 Q20 8 28 4"/><path d="M16 28 L18 24" strokeDasharray="1 2"/></Icon>;
const DistressMed = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4"/><path d="M12 4 Q20 8 28 4"/><path d="M15 30 L18 24" strokeDasharray="1 2"/><path d="M22 26 L24 20" strokeDasharray="1 2"/></Icon>;
const DistressHeavy = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4"/><path d="M12 4 Q20 8 28 4"/><path d="M13 32 L17 24" strokeDasharray="1 1"/><path d="M19 28 L22 20" strokeDasharray="1 1"/><path d="M24 30 L27 22" strokeDasharray="1 1"/></Icon>;
const DistressRipped = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4"/><path d="M12 4 Q20 8 28 4"/><path d="M14 30 Q18 26 16 22 Q20 26 18 30"/><path d="M23 28 Q27 24 25 20 Q29 24 27 28"/></Icon>;

// -- Closure/Back icons
const ClosureZip = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4 Q20 10 12 4"/><path d="M18 6 L18 46 M22 6 L22 46" strokeWidth="1"/><path d="M18 16 L22 20 M18 24 L22 28 M18 32 L22 36"/></Icon>;
const ClosureButton = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4 Q20 10 12 4"/><line x1="20" y1="12" x2="20" y2="46"/><circle cx="20" cy="18" r="2"/><circle cx="20" cy="27" r="2"/><circle cx="20" cy="36" r="2"/></Icon>;
const ClosureWrap2 = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4"/><path d="M12 4 Q20 12 28 4"/><path d="M15 6 L26 46"/></Icon>;
const ClosureLaceUp = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4 Q20 10 12 4"/><path d="M18 12 L22 16 M22 12 L18 16"/><path d="M18 22 L22 26 M22 22 L18 26"/><path d="M18 32 L22 36 M22 32 L18 36"/></Icon>;
const ClosureSnap = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4 Q20 10 12 4"/><circle cx="20" cy="18" r="3"/><circle cx="20" cy="28" r="3"/><circle cx="20" cy="38" r="3"/></Icon>;
const ClosureHook = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4 Q20 10 12 4"/><path d="M18 16 Q18 12 22 12 Q22 16 18 16"/><path d="M18 28 Q18 24 22 24 Q22 28 18 28"/></Icon>;
const ClosureDrawstring = () => <Icon><path d="M12 10 L8 48 L32 48 L28 10"/><path d="M12 10 Q20 14 28 10"/><path d="M16 4 Q16 10 20 12 Q24 10 24 4"/><circle cx="16" cy="4" r="2"/><circle cx="24" cy="4" r="2"/></Icon>;
const ClosureSingle = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4 Q20 10 12 4"/><rect x="16" y="14" width="3" height="4" rx="1"/><rect x="16" y="23" width="3" height="4" rx="1"/><rect x="16" y="32" width="3" height="4" rx="1"/></Icon>;
const ClosureDouble = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4 Q20 10 12 4"/><circle cx="17" cy="18" r="2"/><circle cx="23" cy="18" r="2"/><circle cx="17" cy="28" r="2"/><circle cx="23" cy="28" r="2"/></Icon>;
const ClosureBelted2 = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4 Q20 10 12 4"/><rect x="8" y="22" width="24" height="6" rx="2"/><circle cx="20" cy="25" r="2"/></Icon>;
const BackOpen = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4"/><path d="M12 4 Q20 10 28 4"/><path d="M14 10 L26 10 L26 34 L14 34" strokeDasharray="3 2"/></Icon>;
const BackKeyhole = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4 Q20 10 12 4"/><path d="M20 12 L20 28"/><circle cx="20" cy="10" r="3"/></Icon>;
const BackRegular = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4 Q20 10 12 4"/></Icon>;
const BackTieBack = () => <Icon><path d="M12 4 L8 48 L32 48 L28 4 Q20 10 12 4"/><path d="M16 22 Q20 18 24 22"/><path d="M16 22 L12 30 M24 22 L28 30"/></Icon>;

// -- Pocket icons
const PocketFlap = () => <Icon><path d="M10 4 L6 48 L34 48 L30 4 Q20 10 10 4"/><rect x="12" y="24" width="8" height="8" rx="1"/><path d="M12 24 L20 24 L20 22 L12 22 Z"/></Icon>;
const PocketPatch = () => <Icon><path d="M10 4 L6 48 L34 48 L30 4 Q20 10 10 4"/><rect x="12" y="22" width="9" height="10" rx="1"/></Icon>;
const PocketWelt = () => <Icon><path d="M10 4 L6 48 L34 48 L30 4 Q20 10 10 4"/><path d="M12 28 L22 28"/><line x1="12" y1="26" x2="22" y2="26"/></Icon>;
const PocketCargo = () => <Icon><path d="M10 4 L6 48 L34 48 L30 4 Q20 10 10 4"/><rect x="11" y="22" width="9" height="12" rx="1"/><path d="M13 28 L18 28"/></Icon>;
const PocketHidden = () => <Icon><path d="M10 4 L6 48 L34 48 L30 4 Q20 10 10 4"/><path d="M13 24 Q13 28 16 32" strokeDasharray="2 2"/></Icon>;

// -- Knit icons
const KnitCable = () => <Icon><path d="M10 4 L6 48 L34 48 L30 4 Q20 10 10 4"/><path d="M17 16 Q21 22 17 28 Q21 34 17 40"/><path d="M23 16 Q19 22 23 28 Q19 34 23 40"/></Icon>;
const KnitRibbed = () => <Icon><path d="M10 4 L6 48 L34 48 L30 4 Q20 10 10 4"/><line x1="15" y1="16" x2="15" y2="46"/><line x1="18" y1="16" x2="18" y2="46"/><line x1="22" y1="16" x2="22" y2="46"/><line x1="25" y1="16" x2="25" y2="46"/></Icon>;
const KnitChunky = () => <Icon><path d="M10 4 L6 48 L34 48 L30 4 Q20 10 10 4"/><path d="M12 20 L28 20 M12 28 L28 28 M12 36 L28 36" strokeWidth="3"/></Icon>;
const KnitFine = () => <Icon><path d="M10 4 L6 48 L34 48 L30 4 Q20 10 10 4"/><path d="M12 18 Q16 22 20 18 Q24 14 28 18 M12 24 Q16 28 20 24 Q24 20 28 24 M12 30 Q16 34 20 30 Q24 26 28 30 M12 36 Q16 40 20 36 Q24 32 28 36" strokeWidth="1"/></Icon>;
const KnitJacquard = () => <Icon><path d="M10 4 L6 48 L34 48 L30 4 Q20 10 10 4"/><path d="M14 20 L20 26 L26 20 L20 14 Z"/><path d="M14 34 L20 40 L26 34 L20 28 Z"/></Icon>;
const KnitOpen = () => <Icon><path d="M10 4 L6 48 L34 48 L30 4 Q20 10 10 4"/><circle cx="17" cy="22" r="3"/><circle cx="23" cy="22" r="3"/><circle cx="17" cy="33" r="3"/><circle cx="23" cy="33" r="3"/></Icon>;

// -- Fit icons
const FitCropped = () => <Icon><path d="M12 4 L10 24 L30 24 L28 4 Q20 10 12 4"/></Icon>;
const FitRegular = () => <Icon><path d="M12 4 L10 38 L30 38 L28 4 Q20 10 12 4"/></Icon>;
const FitOversized = () => <Icon><path d="M10 4 L6 40 L34 40 L30 4 Q20 10 10 4"/></Icon>;
const FitFitted = () => <Icon><path d="M14 4 Q16 20 14 36 L26 36 Q24 20 26 4 Q20 8 14 4"/></Icon>;
const FitBoxy = () => <Icon><path d="M10 4 L10 38 L30 38 L30 4 Q20 10 10 4"/></Icon>;
const FitRelaxed = () => <Icon><path d="M11 4 L8 40 L32 40 L29 4 Q20 10 11 4"/></Icon>;
const FitSlim = () => <Icon><path d="M14 4 L12 48 L28 48 L26 4 Q20 8 14 4"/></Icon>;
const FitSkinny = () => <Icon><path d="M15 4 L13 48 L27 48 L25 4 Q20 8 15 4"/></Icon>;
const FitBootcut = () => <Icon><path d="M14 4 L10 30 L8 48 L32 48 L30 30 L26 4 Q20 8 14 4"/></Icon>;
const FitFlared2 = () => <Icon><path d="M14 4 L10 28 L4 48 L36 48 L30 28 L26 4 Q20 8 14 4"/></Icon>;
const FitBoyfriend = () => <Icon><path d="M12 4 L10 48 L30 48 L28 4 Q20 8 12 4"/></Icon>;
const FitMom = () => <Icon><path d="M12 4 L11 48 L29 48 L28 4 Q20 8 12 4"/><line x1="11" y1="12" x2="29" y2="12" strokeWidth="2"/></Icon>;
const FitWideLeg = () => <Icon><path d="M14 4 L8 48 L32 48 L26 4 Q20 8 14 4"/></Icon>;
const FitBiker = () => <Icon><path d="M14 4 Q16 14 14 26 L26 26 Q24 14 26 4 Q20 8 14 4"/></Icon>;

// -- Hem + Slit
const HemStraight = () => <Icon><path d="M12 4 L10 40 L30 40 L28 4 Q20 10 12 4"/><line x1="10" y1="40" x2="30" y2="40" strokeWidth="2"/></Icon>;
const HemCurved = () => <Icon><path d="M12 4 L10 36 Q20 44 30 36 L28 4 Q20 10 12 4"/></Icon>;
const HemRuffled2 = () => <Icon><path d="M12 4 L10 36 L30 36 L28 4 Q20 10 12 4"/><path d="M10 36 Q15 32 20 36 Q25 40 30 36"/></Icon>;
const HemTied = () => <Icon><path d="M12 4 L10 36 L30 36 L28 4 Q20 10 12 4"/><path d="M18 36 Q20 40 22 36"/><path d="M18 36 L16 44 M22 36 L24 44"/></Icon>;
const SlitFront = () => <Icon><path d="M14 4 L10 48 L30 48 L26 4 Q20 8 14 4"/><line x1="20" y1="30" x2="20" y2="48" strokeWidth="2"/></Icon>;
const SlitSide = () => <Icon><path d="M14 4 L10 48 L30 48 L26 4 Q20 8 14 4"/><line x1="10" y1="30" x2="10" y2="48" strokeWidth="2"/></Icon>;
const SlitBack = () => <Icon><path d="M14 4 L10 48 L30 48 L26 4 Q20 8 14 4"/><line x1="20" y1="30" x2="20" y2="48" strokeWidth="2" strokeDasharray="3 2"/></Icon>;
const SlitDouble = () => <Icon><path d="M14 4 L10 48 L30 48 L26 4 Q20 8 14 4"/><line x1="10" y1="30" x2="10" y2="48" strokeWidth="2"/><line x1="30" y1="30" x2="30" y2="48" strokeWidth="2"/></Icon>;

const ICON_MAP = {
  // Necklines
  'Round': NeckRound, 'V-Neck': NeckV, 'Square': NeckSquare, 'Sweetheart': NeckSweetheart,
  'Off-Shoulder': NeckOffShoulder, 'Halter': NeckHalter, 'Boat': NeckBoat, 'Cowl': NeckCowl,
  'Asymmetric': NeckAsymmetric, 'Collar': NeckCollar, 'Peter Pan': NeckPeterPan, 'Smocked': NeckSmocked,
  'Scoop': NeckScoop, 'Wrap': NeckWrap, 'Turtleneck': NeckTurtleneck, 'Polo': NeckPolo, 'Henley': NeckHenley,
  'Crew Neck': NeckCrewNeck, 'Mock Neck': NeckMockNeck, 'Notch': NeckNotch, 'Peak': NeckPeak, 'Shawl': NeckShawl,
  'Hood': NeckHood, 'Stand': NeckStand,
  // Sleeves
  'Sleeveless': SleeveSleeveless, 'Cap': SleeveCap, 'Short': SleeveShort, '3/4': SleeveTQ, 'Long': SleeveLong,
  'Puff': SleevePuff, 'Bishop': SleeveBishop, 'Flutter': SleeveFlutter, 'Bell': SleeveBell, 'Raglan': SleeveRaglan,
  'Drop-Shoulder': SleeveDropShoulder, 'Set-In': SleeveSetIn, 'Regular': SleeveSetIn,
  // Length
  'Micro': LengthMicro, 'Mini': LengthMini, 'Midi': LengthMidi, 'Maxi': LengthMaxi, 'Knee-Length': LengthKnee,
  'Tea-Length': LengthTea, 'Floor-Length': LengthFloor, 'Full-Length': LengthFull, 'Cropped': LengthCropped,
  'Ankle': LengthAnkle, 'Capri': LengthCapri, 'Shorts': LengthShorts, 'Bermuda': LengthBermuda,
  'Hip-Length': LengthHip, 'Longline': LengthLongline, 'Tunic': LengthTunic, 'Thigh': LengthMini,
  // Silhouette
  'A-Line': SilALine, 'Bodycon': SilBodycon, 'Fit & Flare': SilFitFlare, 'Shift': SilShift, 'Ballgown': SilBallgown,
  'Empire': SilEmpire, 'Sheath': SilSheath, 'Peplum': SilPeplum, 'Pleated': SilPleated, 'Flared': SilFlared,
  'Layered': SilLayered, 'Tiered': SilTiered, 'Circle': SilCircle, 'Tulip': SilTulip, 'Straight': SilStraight,
  'Pencil': SilPencil, 'Straight Fit': SilStraight, 'Wide-Leg': FitWideLeg,
  // Rise / Waist
  'High-Rise': RiseHigh, 'Mid-Rise': RiseMid, 'Low-Rise': RiseLow, 'High-Waist': RiseHigh, 'Mid-Waist': RiseMid,
  'Low-Rise': RiseLow, 'Paperbag': WaistPaperbag, 'Elasticated': WaistElastic, 'Belted': WaistBelted,
  'Smocked Waist': WaistSmocked, 'Natural': WaistNatural, 'Dropped': WaistDropped, 'Empire Waist': WaistEmpire,
  'Zip & Button': WaistZipBtn,
  // Distress
  'None': DistressNone, 'Light': DistressLight, 'Medium': DistressMed, 'Heavy': DistressHeavy, 'Ripped': DistressRipped,
  // Closures / Back
  'Zip': ClosureZip, 'Button': ClosureButton, 'Wrap-front': ClosureWrap2, 'Lace-Up': ClosureLaceUp, 'Snap': ClosureSnap,
  'Hook & Eye': ClosureHook, 'Drawstring': ClosureDrawstring, 'Single-Breasted': ClosureSingle, 'Double-Breasted': ClosureDouble,
  'Belted Closure': ClosureBelted2,
  'Open Back': BackOpen, 'Zippered': ClosureZip, 'Button Back': ClosureButton, 'Tie Back': BackTieBack, 'Keyhole': BackKeyhole,
  'Regular Back': BackRegular,
  // Fit
  'Slim': FitSlim, 'Skinny': FitSkinny, 'Bootcut': FitBootcut, 'Relaxed': FitRelaxed, 'Boyfriend': FitBoyfriend,
  'Mom': FitMom, 'Fitted': FitFitted, 'Oversized': FitOversized, 'Boxy': FitBoxy, 'Biker': FitBiker,
  'Regular': FitRegular,
  // Pockets
  'Flap': PocketFlap, 'Patch': PocketPatch, 'Welt': PocketWelt, 'Cargo': PocketCargo, 'Hidden': PocketHidden,
  // Knit
  'Cable': KnitCable, 'Ribbed': KnitRibbed, 'Chunky': KnitChunky, 'Fine': KnitFine, 'Jacquard': KnitJacquard, 'Open Knit': KnitOpen,
  // Hem / Slit
  'Straight Hem': HemStraight, 'Curved Hem': HemCurved, 'Ruffled': HemRuffled2, 'Tied': HemTied,
  'Front': SlitFront, 'Side': SlitSide, 'Back Slit': SlitBack, 'Double': SlitDouble,
};

function FallbackIcon() {
  return <Icon><rect x="10" y="8" width="20" height="36" rx="4" /></Icon>;
}

function svgDataUrlFromComponent(Component) {
  try {
    const svg = renderToStaticMarkup(<Component />);
    const base64 = typeof window !== 'undefined' ? window.btoa(unescape(encodeURIComponent(svg))) : Buffer.from(svg, 'utf8').toString('base64');
    return `data:image/svg+xml;base64,${base64}`;
  } catch (e) {
    return null;
  }
}

export function getCustomizationForType(typeValue, categoryValue, kidAgeRange = null) {
  if (!typeValue) return null;
  const catalogCategory = resolveKidsCatalogCategory(categoryValue, kidAgeRange);
  const womenShirt = catalogCategory === 'women-clothing' && isWomenShirtType(typeValue);
  if (catalogCategory === 'men-clothing' && typeValue === 'tshirt') {
    return [
      { key: 'neckline', label: 'Neckline', opts: Object.keys(MENS_TSHIRT_ASSET_MAP.neckline) },
      { key: 'sleeve', label: 'Sleeve', opts: Object.keys(MENS_TSHIRT_ASSET_MAP.sleeve) },
    ];
  }
  if (catalogCategory === 'men-clothing' && typeValue === 'kurta') {
    return [
      { key: 'neck', label: 'Neck', opts: Object.keys(MENS_KURTA_ASSET_MAP.neck) },
      { key: 'sleeve', label: 'Sleeve', opts: Object.keys(MENS_KURTA_ASSET_MAP.sleeve) },
      { key: 'frontPanel', label: 'Front panel', opts: Object.keys(MENS_KURTA_ASSET_MAP.frontPanel) },
    ];
  }
  if (catalogCategory === 'men-clothing' && typeValue === 'kurta-dhoti') {
    return [
      { key: 'style', label: 'Style', opts: Object.keys(MENS_DHOTI_ASSET_MAP.style) },
    ];
  }
  if (catalogCategory === 'men-clothing' && typeValue === 'pajama') {
    return [
      { key: 'style', label: 'Style', opts: Object.keys(MENS_PAJAMA_ASSET_MAP.style) },
    ];
  }
  if (catalogCategory === 'men-clothing' && typeValue === 'trouser') {
    return [
      { key: 'waist', label: 'Waist area', opts: Object.keys(MENS_PANTS_ASSET_MAP.waist) },
      { key: 'front', label: 'Front construction', opts: Object.keys(MENS_PANTS_ASSET_MAP.front) },
      { key: 'legStructure', label: 'Leg structure', opts: Object.keys(MENS_PANTS_ASSET_MAP.legStructure) },
      { key: 'rise', label: 'Rise', opts: Object.keys(MENS_PANTS_ASSET_MAP.rise) },
      { key: 'lengthPants', label: 'Length (pants)', opts: Object.keys(MENS_PANTS_ASSET_MAP.lengthPants) },
      { key: 'lengthShorts', label: 'Length (shorts)', opts: Object.keys(MENS_PANTS_ASSET_MAP.lengthShorts) },
      { key: 'lengthBoyShort', label: 'Length (boy short)', opts: Object.keys(MENS_PANTS_ASSET_MAP.lengthBoyShort) },
      { key: 'pocketFront', label: 'Front pockets', opts: Object.keys(MENS_PANTS_ASSET_MAP.pocketFront) },
      { key: 'pocketBack', label: 'Back pockets', opts: Object.keys(MENS_PANTS_ASSET_MAP.pocketBack) },
    ];
  }
  if (catalogCategory === 'men-clothing' && typeValue === 'shirt') {
    return [
      { key: 'collar', label: 'Collar', opts: Object.keys(MENS_SHIRT_ASSET_MAP.collar) },
      { key: 'placket', label: 'Placket', opts: Object.keys(MENS_SHIRT_ASSET_MAP.placket) },
      { key: 'cuff', label: 'Cuffs', opts: Object.keys(MENS_SHIRT_ASSET_MAP.cuff) },
      { key: 'sleeve', label: 'Sleeves', opts: Object.keys(MENS_SHIRT_ASSET_MAP.sleeve) },
      { key: 'fit', label: 'Fit', opts: Object.keys(MENS_SHIRT_ASSET_MAP.fit) },
      { key: 'hem', label: 'Hems', opts: Object.keys(MENS_SHIRT_ASSET_MAP.hem) },
      { key: 'backPleat', label: 'Back pleats', opts: Object.keys(MENS_SHIRT_ASSET_MAP.backPleat) },
      { key: 'pocket', label: 'Pockets', opts: Object.keys(MENS_SHIRT_ASSET_MAP.pocket) },
    ];
  }
  if (catalogCategory === 'men-clothing' && typeValue === 'jacket') {
    return [
      { key: 'collar', label: 'Collar', opts: Object.keys(MENS_JACKET_ASSET_MAP.collar) },
      { key: 'sleeve', label: 'Sleeve option', opts: Object.keys(MENS_JACKET_ASSET_MAP.sleeve) },
      { key: 'outerShell', label: 'Outer shell', opts: Object.keys(MENS_JACKET_ASSET_MAP.outerShell) },
    ];
  }
  if (catalogCategory === 'men-clothing' && typeValue === 'blazer') {
    return [
      { key: 'collar', label: 'Collars', opts: Object.keys(MENS_BLAZER_ASSET_MAP.collar) },
      { key: 'buttons', label: 'Buttons', opts: Object.keys(MENS_BLAZER_ASSET_MAP.buttons) },
      { key: 'vents', label: 'Vents', opts: Object.keys(MENS_BLAZER_ASSET_MAP.vents) },
      { key: 'pockets', label: 'Pockets', opts: Object.keys(MENS_BLAZER_ASSET_MAP.pockets) },
      { key: 'hem', label: 'Hems', opts: Object.keys(MENS_BLAZER_ASSET_MAP.hem) },
    ];
  }
  if (catalogCategory === 'men-clothing' && typeValue === 'waistcoat') {
    return [
      { key: 'traditional', label: 'Traditional waistcoat', opts: Object.keys(MENS_WAISTCOAT_ASSET_MAP.traditional) },
      { key: 'nehruJacket', label: 'Nehru jacket', opts: Object.keys(MENS_WAISTCOAT_ASSET_MAP.nehruJacket) },
      { key: 'westernTuxedo', label: 'Western / tuxedo', opts: Object.keys(MENS_WAISTCOAT_ASSET_MAP.westernTuxedo) },
    ];
  }
  if (catalogCategory === 'men-clothing' && typeValue === 'cordset') {
    return [
      { key: 'style', label: 'Coord set style', opts: Object.keys(MENS_CORDSET_ASSET_MAP.style) },
    ];
  }
  if (typeValue === 'socks') {
    return [
      { key: 'length', label: 'Length', opts: Object.keys(SOCKS_ASSET_MAP.length) },
      { key: 'cuffStyle', label: 'Cuff style', opts: Object.keys(SOCKS_ASSET_MAP.cuffStyle) },
      { key: 'toeStyle', label: 'Toe style', opts: Object.keys(SOCKS_ASSET_MAP.toeStyle) },
    ];
  }
  if (
    (categoryValue === 'kids-boy' || categoryValue === 'kids-girl') &&
    kidAgeRange === 'toddlers' &&
    typeValue === 'toddler-dress'
  ) {
    return [{ key: 'style', label: 'Toddler style', opts: Object.keys(KIDS_BOY_TODDLER_ASSET_MAP.style) }];
  }
  if (catalogCategory === 'women-clothing' && typeValue === 'dress') {
    return [
      { key: 'necklineFront', label: 'Front neckline', opts: Object.keys(WOMEN_DRESS_ASSET_MAP.necklineFront) },
      { key: 'necklineBack', label: 'Back neckline', opts: Object.keys(WOMEN_DRESS_ASSET_MAP.necklineBack) },
      { key: 'sleeve', label: 'Sleeves', opts: Object.keys(WOMEN_DRESS_ASSET_MAP.sleeve) },
      { key: 'length', label: 'Length', opts: Object.keys(WOMEN_DRESS_ASSET_MAP.length) },
      { key: 'bodice', label: 'Bodice', opts: Object.keys(WOMEN_DRESS_ASSET_MAP.bodice) },
      { key: 'bodyCutOut', label: 'Body cut-outs', opts: Object.keys(WOMEN_DRESS_ASSET_MAP.bodyCutOut) },
      { key: 'waistAttachment', label: 'Waist attachments', opts: Object.keys(WOMEN_DRESS_ASSET_MAP.waistAttachment) },
      { key: 'waistPlacement', label: 'Waist placement', opts: Object.keys(WOMEN_DRESS_ASSET_MAP.waistPlacement) },
      { key: 'shoulderDetail', label: 'Shoulder details', opts: Object.keys(WOMEN_DRESS_ASSET_MAP.shoulderDetail) },
      { key: 'surfaceVolume', label: 'Surface & volume details', opts: Object.keys(WOMEN_DRESS_ASSET_MAP.surfaceVolume) },
      { key: 'strap', label: 'Strap variations', opts: Object.keys(WOMEN_DRESS_ASSET_MAP.strap) },
      { key: 'flareVariation', label: 'Flare variation', opts: Object.keys(WOMEN_DRESS_ASSET_MAP.flareVariation) },
      { key: 'slitVariationLength', label: 'Slit variation — Length', opts: Object.keys(WOMEN_DRESS_ASSET_MAP.slitVariationLength) },
      { key: 'slitVariationTypes', label: 'Slit variation — Types', opts: Object.keys(WOMEN_DRESS_ASSET_MAP.slitVariationTypes) },
    ];
  }
  if (catalogCategory === 'women-clothing' && typeValue === 'top') {
    return [
      { key: 'neckline', label: 'Neckline', opts: Object.keys(WOMEN_TOP_ASSET_MAP.neckline) },
      { key: 'sleeve', label: 'Sleeves', opts: Object.keys(WOMEN_TOP_ASSET_MAP.sleeve) },
      { key: 'hem', label: 'Hems', opts: Object.keys(WOMEN_TOP_ASSET_MAP.hem) },
      { key: 'yoke', label: 'Yoke', opts: Object.keys(WOMEN_TOP_ASSET_MAP.yoke) },
      { key: 'topLength', label: 'Length', opts: Object.keys(WOMEN_TOP_ASSET_MAP.topLength) },
      { key: 'bodice', label: 'Bodice', opts: Object.keys(WOMEN_TOP_ASSET_MAP.bodice) },
      { key: 'panels', label: 'Panels', opts: Object.keys(WOMEN_TOP_ASSET_MAP.panels) },
      { key: 'pocket', label: 'Pockets', opts: Object.keys(WOMEN_TOP_ASSET_MAP.pocket) },
      { key: 'dart', label: 'Darts', opts: Object.keys(WOMEN_TOP_ASSET_MAP.dart) },
      { key: 'strap', label: 'Strap variations', opts: Object.keys(WOMEN_TOP_ASSET_MAP.strap) },
      { key: 'openingCloser', label: 'Openings & closures', opts: Object.keys(WOMEN_TOP_ASSET_MAP.openingCloser) },
    ];
  }
  if (catalogCategory === 'women-clothing' && typeValue === 'jeans') {
    return [
      { key: 'silhouette', label: 'Silhouette', opts: Object.keys(WOMEN_PANT_ASSET_MAP.silhouette) },
      { key: 'length', label: 'Length', opts: Object.keys(WOMEN_PANT_ASSET_MAP.length) },
      { key: 'rise', label: 'Rise', opts: Object.keys(WOMEN_PANT_ASSET_MAP.rise) },
      { key: 'waist', label: 'Waist', opts: Object.keys(WOMEN_PANT_ASSET_MAP.waist) },
      { key: 'opening', label: 'Opening', opts: Object.keys(WOMEN_PANT_ASSET_MAP.opening) },
      { key: 'pocket', label: 'Pockets', opts: Object.keys(WOMEN_PANT_ASSET_MAP.pocket) },
      { key: 'yoke', label: 'Yoke', opts: Object.keys(WOMEN_PANT_ASSET_MAP.yoke) },
    ];
  }
  if (catalogCategory === 'women-clothing' && typeValue === 'skirt') {
    return [
      { key: 'shape', label: 'Shape', opts: Object.keys(WOMEN_SKIRT_ASSET_MAP.shape) },
      { key: 'flair', label: 'Flair', opts: Object.keys(WOMEN_SKIRT_ASSET_MAP.flair) },
      { key: 'length', label: 'Length', opts: Object.keys(WOMEN_SKIRT_ASSET_MAP.length) },
      { key: 'waist', label: 'Waist', opts: Object.keys(WOMEN_SKIRT_ASSET_MAP.waist) },
    ];
  }
  if (catalogCategory === 'women-clothing' && typeValue === 'trouser') {
    return [
      { key: 'style', label: 'Style', opts: Object.keys(WOMEN_TRADITIONAL_BOTTOMS_ASSET_MAP.style) },
      { key: 'waistband', label: 'Waist band', opts: Object.keys(WOMEN_TRADITIONAL_BOTTOMS_ASSET_MAP.waistband) },
      { key: 'pocket', label: 'Pockets', opts: Object.keys(WOMEN_TRADITIONAL_BOTTOMS_ASSET_MAP.pocket) },
    ];
  }
  if (catalogCategory === 'women-clothing' && typeValue === 'lehenga') {
    return [
      { key: 'pleatedType', label: 'Pleated lehenga type', opts: Object.keys(WOMEN_LEHENGA_ASSET_MAP.pleatedType) },
      { key: 'hem', label: 'Hem variation', opts: Object.keys(WOMEN_LEHENGA_ASSET_MAP.hem) },
      { key: 'waistband', label: 'Waist band', opts: Object.keys(WOMEN_LEHENGA_ASSET_MAP.waistband) },
      { key: 'pocket', label: 'Pockets', opts: Object.keys(WOMEN_LEHENGA_ASSET_MAP.pocket) },
    ];
  }
  if (catalogCategory === 'women-clothing' && typeValue === 'blouses') {
    return [
      { key: 'sleeve', label: 'Sleeves', opts: Object.keys(WOMEN_BLOUSES_ASSET_MAP.sleeve) },
      { key: 'backNeck', label: 'Back neck', opts: Object.keys(WOMEN_BLOUSES_ASSET_MAP.backNeck) },
      { key: 'closure', label: 'Closure', opts: Object.keys(WOMEN_BLOUSES_ASSET_MAP.closure) },
      { key: 'frontOpening', label: 'Front opening', opts: Object.keys(WOMEN_BLOUSES_ASSET_MAP.frontOpening) },
    ];
  }
  if (catalogCategory === 'women-clothing' && typeValue === 'saree-styles') {
    return [
      { key: 'drapingStyle', label: 'Draping style', opts: Object.keys(WOMEN_SAREE_STYLES_ASSET_MAP.drapingStyle) },
    ];
  }
  if (catalogCategory === 'women-clothing' && typeValue === 'night-wear') {
    return [
      { key: 'frontPanel', label: 'Front panel', opts: Object.keys(WOMEN_NIGHT_WEAR_ASSET_MAP.frontPanel) },
    ];
  }
  if (catalogCategory === 'women-clothing' && typeValue === 'lingerie') {
    return [
      { key: 'style', label: 'Style', opts: Object.keys(WOMEN_LINGERIE_ASSET_MAP.style) },
    ];
  }
  if (catalogCategory === 'women-clothing' && typeValue === 'blazer') {
    return [
      { key: 'shape', label: 'Shape', opts: Object.keys(WOMEN_BLAZER_ASSET_MAP.shape) },
      { key: 'length', label: 'Length', opts: Object.keys(WOMEN_BLAZER_ASSET_MAP.length) },
      { key: 'neckline', label: 'Neckline & lapels', opts: Object.keys(WOMEN_BLAZER_ASSET_MAP.neckline) },
      { key: 'sleeve', label: 'Sleeves', opts: Object.keys(WOMEN_BLAZER_ASSET_MAP.sleeve) },
    ];
  }
  if (catalogCategory === 'women-clothing' && typeValue === 'hoodies') {
    return [
      { key: 'style', label: 'Style', opts: Object.keys(WOMEN_HOODIES_ASSET_MAP.style) },
    ];
  }
  if (catalogCategory === 'women-clothing' && typeValue === 'layering') {
    return [
      { key: 'style', label: 'Style', opts: Object.keys(WOMEN_LAYERING_ASSET_MAP.style) },
    ];
  }
  if (catalogCategory === 'women-clothing' && typeValue === 'jacket') {
    return [
      { key: 'style', label: 'Jacket style', opts: Object.keys(WOMEN_JACKET_ASSET_MAP.style) },
      { key: 'sleeve', label: 'Sleeves', opts: Object.keys(WOMEN_JACKET_ASSET_MAP.sleeve) },
      { key: 'neckline', label: 'Neckline', opts: Object.keys(WOMEN_JACKET_ASSET_MAP.neckline) },
    ];
  }
  if (womenShirt) {
    return [
      { key: 'neckline', label: 'Neckline', opts: Object.keys(WOMEN_SHIRT_ASSET_MAP.neckline) },
      { key: 'fit', label: 'Fit', opts: Object.keys(WOMEN_SHIRT_ASSET_MAP.fit) },
      { key: 'hem', label: 'Hem', opts: Object.keys(WOMEN_SHIRT_ASSET_MAP.hem) },
      { key: 'drape', label: 'Drape', opts: Object.keys(WOMEN_SHIRT_ASSET_MAP.drape) },
    ];
  }
  return ATTRIBUTES[typeValue] || null;
}

/** Icon + gradient per garment kind (top-level only; no subtype UI). */
function getProductTypeVisual(type) {
  const hay = `${String(type.value || '').toLowerCase().replace(/\s+/g, ' ')} ${String(type.label || '').toLowerCase()}`;
  if (/t-?shirt|tshirt|\bpolo\b/.test(hay)) return { Icon: Shirt, bg: 'from-sky-500 to-blue-600' };
  if (/\bshirt\b/.test(hay)) return { Icon: Shirt, bg: 'from-blue-500 to-indigo-700' };
  if (/dress|frock|gown/.test(hay)) return { Icon: Sparkles, bg: 'from-fuchsia-500 to-purple-600' };
  if (/skirt/.test(hay)) return { Icon: Layers, bg: 'from-pink-400 to-fuchsia-600' };
  if (/shorts/.test(hay)) return { Icon: Footprints, bg: 'from-lime-600 to-green-700' };
  if (/jean|denim/.test(hay)) return { Icon: CircleDot, bg: 'from-blue-800 to-slate-900' };
  if (/trouser|chino|\bcargo\b/.test(hay)) return { Icon: Gem, bg: 'from-zinc-500 to-zinc-800' };
  if (/pajama/.test(hay)) return { Icon: Gem, bg: 'from-indigo-500 to-slate-700' };
  if (/jacket|bomber|windbreaker/.test(hay)) return { Icon: Wind, bg: 'from-teal-600 to-emerald-700' };
  if (/sweater|cardigan|pullover|hoodie/.test(hay)) return { Icon: Shirt, bg: 'from-orange-400 to-amber-600' };
  if (/kurta|kameez|salwar|dupatta|anarkali|lehenga|dhoti/.test(hay)) return { Icon: Heart, bg: 'from-amber-500 to-orange-600' };
  if (/saree|sari/.test(hay)) return { Icon: Layers, bg: 'from-emerald-500 to-teal-600' };
  if (/sherwani|nehru|waistcoat|bandhgala/.test(hay)) return { Icon: Crown, bg: 'from-violet-600 to-indigo-800' };
  if (/suit|tuxedo|blazer/.test(hay)) return { Icon: Briefcase, bg: 'from-slate-600 to-slate-900' };
  if (/\btop\b/.test(hay)) return { Icon: Shirt, bg: 'from-rose-500 to-pink-600' };
  if (/sock/.test(hay)) return { Icon: Footprints, bg: 'from-amber-500 to-orange-500' };
  return DEFAULT_VISUAL;
}

function getCategoryTypeTileVisual(categoryValue, typeValue, kidAgeRange = null) {
  let img = null;
  if (
    (categoryValue === 'kids-boy' || categoryValue === 'kids-girl') &&
    kidAgeRange === 'toddlers' &&
    typeValue === 'toddler-dress'
  ) {
    img = categoryValue === 'kids-girl' ? KIDS_GIRL_TODDLER_PREVIEW : KIDS_BOY_TODDLER_PREVIEW;
  }
  const catalogCategory = resolveKidsCatalogCategory(categoryValue, kidAgeRange);
  if (!img && catalogCategory === 'women-clothing') {
    img = getWomenGarmentTypeIcon(typeValue);
  } else if (!img && catalogCategory === 'men-clothing') {
    img = getMenGarmentTypeIcon(typeValue);
  }
  if (img) {
    return { img, bg: GARMENT_TYPE_TILE_BG };
  }
  return null;
}

/** e.g. women-clothing + Pant (value jeans) → "women-pant" (category prefix + display label slug). */
function formatCustomizeTabLabel(t) {
  if (!t) return '';
  const cat = String(t.category || '').trim();
  const prefix = cat ? cat.split('-')[0].toLowerCase() : '';
  const displayName = String(t.finalLabel || t.label || t.value || '').trim();
  const typeSlug = displayName
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
  if (prefix && typeSlug) return `${prefix}-${typeSlug}`;
  return t.finalLabel || t.label || String(t.value || '');
}

function customizeTabTitle(t) {
  if (!t) return '';
  const bits = [t.categoryLabel, t.finalLabel || t.label].filter(Boolean);
  if (bits.length) return bits.join(' — ');
  return formatCustomizeTabLabel(t);
}

function GarmentInspirationCard({
  categoryValue,
  typeValue,
  label,
  customizationCount = 0,
  customizationPreviewItems = null,
  isActive = false,
  onCustomise,
  onSelect,
  onRemove,
  showSectionLabel = false,
  banner = false,
  kidAgeRange = null,
}) {
  const inspirationSrc = getGarmentInspirationImage(categoryValue, typeValue, kidAgeRange);
  const previewItems = Array.isArray(customizationPreviewItems)
    ? customizationPreviewItems.filter((item) => item?.thumbSrc)
    : [];
  const showCustomizations = previewItems.length > 0;
  const useInspiration =
    !showCustomizations && Boolean(inspirationSrc && hasGarmentInspirationCategory(categoryValue));
  const { Icon, bg } = getProductTypeVisual({ value: typeValue, label });
  const categoryTile = getCategoryTypeTileVisual(categoryValue, typeValue, kidAgeRange);
  const tileBg = categoryTile?.bg || bg;

  const rootClass = banner
    ? `shrink-0 border-b border-purple-100 bg-gradient-to-b from-purple-50/80 to-white px-3 py-3 sm:px-4${onRemove ? ' relative' : ''}`
    : [
        'group relative flex flex-col overflow-hidden rounded-2xl border bg-gradient-to-b from-purple-50/80 to-white transition-all',
        isActive ? 'border-purple-500 ring-2 ring-purple-300 shadow-md' : 'border-purple-100 hover:border-purple-200 hover:shadow-sm',
        onSelect ? 'cursor-pointer' : '',
      ].join(' ');

  const imageWrapClass = banner
    ? 'relative flex min-h-[10rem] items-center justify-center rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-100 sm:min-h-[12rem]'
    : 'relative mx-3 mt-3 flex min-h-[9rem] items-center justify-center rounded-xl bg-white p-2 shadow-sm ring-1 ring-slate-100 sm:min-h-[10rem]';

  const imageClass = banner
    ? 'max-h-[min(38vh,320px)] w-full object-contain'
    : 'max-h-44 w-full object-contain sm:max-h-52';

  const customizationGridClass =
    previewItems.length === 1
      ? 'grid w-full grid-cols-1 gap-2'
      : previewItems.length === 2
        ? 'grid w-full grid-cols-2 gap-2'
        : 'grid w-full grid-cols-2 gap-2 sm:grid-cols-3';

  const inner = (
    <>
      {showSectionLabel ? (
        <p className="mb-2 px-3 pt-3 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-purple-500 sm:px-0 sm:pt-0">
          {showCustomizations ? 'Customisations' : 'Inspiration'}
        </p>
      ) : null}
      <div className={banner && showSectionLabel ? 'px-0' : ''}>
        <div className={imageWrapClass}>
          {showCustomizations ? (
            <div className={customizationGridClass} aria-label="Selected customisations">
              {previewItems.map((item) => (
                <div
                  key={item.id}
                  className="flex min-h-0 flex-col items-center justify-center rounded-lg bg-slate-50/80 p-1.5 ring-1 ring-slate-100"
                  title={[item.attrLabel, item.opt].filter(Boolean).join(' — ')}
                >
                  <img
                    src={item.thumbSrc}
                    alt={item.opt || item.attrLabel || 'Customisation'}
                    className={
                      previewItems.length === 1
                        ? imageClass
                        : 'h-20 w-full object-contain sm:h-24'
                    }
                    loading="lazy"
                  />
                  {previewItems.length > 1 && item.opt ? (
                    <p className="mt-1 w-full truncate text-center text-[10px] font-medium text-slate-600">
                      {item.opt}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          ) : useInspiration ? (
            <img
              src={encodeAssetPath(inspirationSrc)}
              alt={label ? `${label} inspiration` : 'Garment inspiration'}
              className={imageClass}
              loading="lazy"
            />
          ) : (
            <span
              className={`flex h-full min-h-[8rem] w-full items-center justify-center rounded-xl bg-gradient-to-br ${tileBg}`}
            >
              {categoryTile ? (
                <GarmentTypeTileIcon tile={categoryTile} Icon={Icon} />
              ) : (
                <Icon className="h-10 w-10 text-white" strokeWidth={2} />
              )}
            </span>
          )}
          {onCustomise ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCustomise();
              }}
              className="absolute bottom-2 right-2 z-10 inline-flex items-center gap-1 rounded-lg bg-purple-600 px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-md transition hover:bg-purple-700 active:scale-95"
            >
              <Edit3 size={12} aria-hidden />
              Customise
              {customizationCount > 0 ? (
                <span className="rounded-full bg-white/25 px-1.5 py-0.5 text-[10px] leading-none">
                  {customizationCount}
                </span>
              ) : null}
            </button>
          ) : null}
        </div>
        {label && !banner ? (
          <div className="px-3 pb-3 pt-2 text-center">
            <p className="truncate text-sm font-semibold text-slate-900">{label}</p>
            {customizationCount > 0 ? (
              <p className="mt-0.5 text-xs text-purple-600">
                {customizationCount} customisation{customizationCount !== 1 ? 's' : ''} selected
              </p>
            ) : onCustomise ? (
              <p className="mt-0.5 text-xs text-slate-500">Tap Customise for options</p>
            ) : null}
          </div>
        ) : null}
        {label && banner ? (
          <p className="mt-2 truncate text-center text-xs font-semibold text-slate-700">{label}</p>
        ) : null}
      </div>
    </>
  );

  return (
    <div
      className={rootClass}
      onClick={onSelect && !banner ? onSelect : undefined}
    >
      {onRemove ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="absolute right-2 top-2 z-20 inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200/90 bg-white/95 text-slate-500 shadow-sm transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 active:scale-95"
          title={`Remove ${label || 'product type'}`}
          aria-label={`Remove ${label || 'product type'}`}
        >
          <X size={14} strokeWidth={2.5} aria-hidden />
        </button>
      ) : null}
      {inner}
    </div>
  );
}

function CustomizationPreviewPanel({
  selectedOpt,
  previewSrc,
  currentAttrLabel,
  primaryInFullscreen = false,
  primaryBesideSidePanel = false,
  isUserReference = false,
  compactMobile = false,
}) {
  const showPlaceholder = !selectedOpt || !previewSrc;
  const rootClass = isUserReference
    ? primaryBesideSidePanel
      ? 'mt-0 flex shrink-0 flex-col rounded-2xl border border-slate-200 bg-white p-3 sm:p-4'
      : primaryInFullscreen
        ? 'mt-1 flex shrink-0 flex-col rounded-2xl border border-slate-200 bg-white p-4'
        : 'mt-1 shrink-0 flex flex-col rounded-2xl border border-slate-200 bg-white p-4'
    : primaryBesideSidePanel
      ? 'mt-0 flex h-full min-h-0 flex-1 flex-col rounded-2xl border border-slate-200 bg-white p-3 sm:p-4'
      : primaryInFullscreen
        ? 'mt-1 flex shrink-0 flex-col rounded-2xl border border-slate-200 bg-white p-4'
        : compactMobile
          ? 'mt-1 flex min-h-[11rem] shrink-0 flex-col rounded-2xl border border-slate-200 bg-white p-3'
          : 'mt-1 flex min-h-0 flex-1 flex-col rounded-2xl border border-slate-200 bg-white p-4';

  return (
    <div className={rootClass}>
      <div className="mb-3 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-slate-500">
        Preview
        {(primaryInFullscreen || primaryBesideSidePanel) && currentAttrLabel ? (
          <span className="font-medium normal-case tracking-normal text-slate-400"> — {currentAttrLabel}</span>
        ) : null}
      </div>
      {showPlaceholder ? (
        <div
          className={
            primaryBesideSidePanel
              ? 'flex min-h-0 flex-1 flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/90 px-4 py-8'
              : primaryInFullscreen
                ? 'flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/90 px-4 py-8'
                : 'flex min-h-0 flex-1 flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/90 px-4 py-10'
          }
        >
          <MousePointerClick className="h-11 w-11 shrink-0 text-purple-300" strokeWidth={1.35} aria-hidden />
          <p className="max-w-[19rem] text-center text-sm font-medium leading-snug text-slate-600">
            Click an option above to see the preview here
          </p>
        </div>
      ) : (
        <div
          className={
            isUserReference
              ? 'flex flex-col items-center justify-center gap-2'
              : primaryBesideSidePanel
                ? 'flex min-h-0 flex-1 flex-col items-center justify-center gap-3'
                : primaryInFullscreen
                  ? 'flex flex-col items-center justify-center gap-3'
                  : 'flex min-h-0 flex-1 flex-col items-center justify-center gap-4'
          }
        >
          <div
            className={
              isUserReference
                ? 'flex max-h-48 w-full items-center justify-center overflow-hidden rounded-2xl bg-slate-50 p-3 sm:max-h-52'
                : primaryBesideSidePanel
                  ? 'relative min-h-[12rem] w-full flex-1 overflow-hidden rounded-2xl bg-slate-50'
                  : primaryInFullscreen
                    ? 'relative h-[min(42vh,400px)] min-h-[11rem] w-full overflow-hidden rounded-2xl bg-slate-50'
                    : 'flex w-full flex-1 items-center justify-center overflow-hidden rounded-2xl bg-slate-50 p-3'
            }
          >
            <img
              src={previewSrc}
              alt={selectedOpt}
              className={
                isUserReference
                  ? 'max-h-40 max-w-full object-contain sm:max-h-44'
                  : primaryBesideSidePanel || primaryInFullscreen
                    ? 'absolute inset-0 box-border h-full w-full object-contain p-3'
                    : 'h-full max-h-[420px] w-full object-contain'
              }
              loading="lazy"
            />
          </div>
          <div className="w-full min-w-0 text-center">
            <div className="truncate text-sm font-semibold text-slate-900">{selectedOpt}</div>
            {currentAttrLabel ? (
              <div className="mt-1 text-xs text-slate-500">
                {isUserReference ? 'Custom reference' : `Selected ${currentAttrLabel.toLowerCase()}`}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function getUserReferenceDisplayName(userRef) {
  const name = String(userRef?.customName || '').trim();
  return name || 'Custom option';
}

function buildUserReferenceOnlyEntry(userRef) {
  const url = toAbsoluteUrl(userRef.imageUrl);
  return {
    option: getUserReferenceDisplayName(userRef),
    referenceImage: userRef.imageUrl,
    referenceImageUrl: url,
    mimeType: userRef.mimeType || 'image/jpeg',
    userUploaded: true,
  };
}

function resolveCustomizationReferenceEntry(
  opt,
  attrKey,
  { categoryValue, typeValue, kidAgeRange, isReferenceImageTypeActive, userRef }
) {
  if (userRef?.imageUrl) {
    return buildUserReferenceOnlyEntry(userRef);
  }
  const referenceAsset = isReferenceImageTypeActive
    ? getReferenceAsset({
        categoryValue,
        typeValue,
        attrKey,
        opt,
        kidAgeRange,
      })
    : null;
  if (referenceAsset) {
    const mimeType = getMimeTypeFromAssetPath(referenceAsset);
    return {
      option: opt,
      referenceImage: referenceAsset,
      referenceImageUrl: toAbsoluteUrl(referenceAsset),
      mimeType,
    };
  }
  const IconComp = ICON_MAP[opt] || FallbackIcon;
  const dataUrl = svgDataUrlFromComponent(IconComp);
  return dataUrl
    ? {
        option: opt,
        referenceImage: dataUrl,
        referenceImageUrl: toAbsoluteUrl(dataUrl),
        mimeType: 'image/svg+xml',
      }
    : { option: opt, referenceImage: null, referenceImageUrl: null, mimeType: 'image/svg+xml' };
}

function buildFullscreenCustomizationPreviewItems(schema, perType, ctx) {
  const {
    isReferenceImageTypeActive,
    activeTypeCategoryValue,
    activeTypeValue,
    kidAgeRange,
    userReferences = {},
  } = ctx;
  const items = [];
  if (!schema || !perType) return items;
  for (const attr of schema) {
    const userRef = userReferences[attr.key];
    if (userRef?.imageUrl) {
      items.push({
        id: `${attr.key}::__user_ref__`,
        attrKey: attr.key,
        attrLabel: attr.label,
        opt: getUserReferenceDisplayName(userRef),
        thumbSrc: toAbsoluteUrl(userRef.imageUrl),
        isUserReference: true,
      });
      continue;
    }

    const arr = perType[attr.key];
    if (!Array.isArray(arr) || arr.length === 0) continue;

    for (const opt of arr) {
      if (opt == null || String(opt).trim() === '') continue;
      const entry = resolveCustomizationReferenceEntry(opt, attr.key, {
        categoryValue: activeTypeCategoryValue,
        typeValue: activeTypeValue,
        kidAgeRange,
        isReferenceImageTypeActive,
        userRef,
      });
      const thumbSrc =
        entry.referenceImageUrl ||
        (entry.referenceImage ? toAbsoluteUrl(entry.referenceImage) : null) ||
        svgDataUrlFromComponent(ICON_MAP[opt] || FallbackIcon);
      items.push({
        id: `${attr.key}::${opt}`,
        attrKey: attr.key,
        attrLabel: attr.label,
        opt: String(opt),
        thumbSrc,
        isUserReference: Boolean(entry.userUploaded),
      });
    }
  }
  return items;
}

function getCustomizationAttrPreviewSrc(attrKey, selectedOpt, ctx) {
  const {
    userReferences = {},
    isReferenceImageTypeActive,
    activeTypeCategoryValue,
    activeTypeValue,
    kidAgeRange,
  } = ctx;
  const userRef = userReferences[attrKey];
  if (userRef?.imageUrl) {
    return {
      src: toAbsoluteUrl(userRef.imageUrl),
      label: getUserReferenceDisplayName(userRef),
      isUserReference: true,
    };
  }
  if (!selectedOpt) return { src: null, label: null, isUserReference: false };
  const selectedReferenceSrc = isReferenceImageTypeActive
    ? getReferenceAsset({
        categoryValue: activeTypeCategoryValue,
        typeValue: activeTypeValue,
        attrKey,
        opt: selectedOpt,
        kidAgeRange,
      })
    : null;
  const SelectedIcon = ICON_MAP[selectedOpt] || FallbackIcon;
  const fallbackSvg = svgDataUrlFromComponent(SelectedIcon);
  return {
    src: selectedReferenceSrc ? toAbsoluteUrl(selectedReferenceSrc) : fallbackSvg,
    label: selectedOpt,
    isUserReference: false,
  };
}

function CustomizationUserReferenceUpload({
  attrKey,
  attrLabel,
  userRef,
  isUploading,
  inputRef,
  onUpload,
  onRemove,
  onCustomNameChange,
}) {
  const triggerUpload = () => {
    if (inputRef?.current) inputRef.current.click();
  };

  return (
    <div className="mb-3 rounded-xl border border-dashed border-purple-200/90 bg-purple-50/50 p-3">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={onUpload}
      />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-purple-900">Your reference</p>
          <p className="mt-0.5 text-[10px] leading-snug text-slate-600">
            Optional image for {attrLabel}. Used instead of default references when generating.
          </p>
        </div>
        {userRef?.imageUrl ? (
          <div className="flex shrink-0 items-center gap-2">
            <img
              src={toAbsoluteUrl(userRef.imageUrl)}
              alt={`Your reference for ${attrLabel}`}
              className="h-14 w-14 rounded-lg border border-purple-200 object-cover shadow-sm"
            />
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={triggerUpload}
                disabled={isUploading}
                className="inline-flex items-center justify-center gap-1 rounded-lg border border-purple-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-purple-700 transition hover:bg-purple-50 disabled:opacity-60"
              >
                {isUploading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <Upload className="h-3.5 w-3.5" aria-hidden />
                )}
                Replace
              </button>
              <button
                type="button"
                onClick={onRemove}
                disabled={isUploading}
                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700 disabled:opacity-60"
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={triggerUpload}
            disabled={isUploading}
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-purple-300 bg-white px-3 py-2 text-xs font-semibold text-purple-700 shadow-sm transition hover:bg-purple-50 disabled:opacity-60"
          >
            {isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Upload className="h-4 w-4" aria-hidden />
            )}
            Upload reference
          </button>
        )}
      </div>
      {userRef?.imageUrl ? (
        <div className="mt-3 border-t border-purple-100/80 pt-3">
          <label
            htmlFor={`custom-ref-name-${attrKey}`}
            className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-purple-700"
          >
            Option name
          </label>
          <input
            id={`custom-ref-name-${attrKey}`}
            type="text"
            value={userRef.customName || ''}
            onChange={(e) => onCustomNameChange(e.target.value)}
            placeholder={`e.g. My ${attrLabel.toLowerCase()} style`}
            maxLength={80}
            className="w-full rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-200"
          />
          <p className="mt-1 text-[10px] text-slate-500">
            Shown in preview and used when generating. Preset options are hidden while a custom reference is active.
          </p>
        </div>
      ) : null}
      {userRef?.fileName ? (
        <p className="mt-2 truncate text-[10px] text-slate-500" title={userRef.fileName}>
          {userRef.fileName}
        </p>
      ) : null}
    </div>
  );
}

/** Horizontal strip with corner arrows for easier scrolling (customise modal). */
function HorizontalScrollWithArrows({ children, scrollClassName = '', className = '' }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const rafRef = useRef(null);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const nextLeft = scrollLeft > 2;
    const nextRight = scrollLeft + clientWidth < scrollWidth - 2;
    setCanScrollLeft((prev) => (prev === nextLeft ? prev : nextLeft));
    setCanScrollRight((prev) => (prev === nextRight ? prev : nextRight));
  }, []);

  const scheduleScrollStateUpdate = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      updateScrollState();
    });
  }, [updateScrollState]);

  useEffect(() => {
    const el = scrollRef.current;
    scheduleScrollStateUpdate();
    if (!el) return undefined;
    el.addEventListener('scroll', scheduleScrollStateUpdate, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleScrollStateUpdate) : null;
    ro?.observe(el);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      el.removeEventListener('scroll', scheduleScrollStateUpdate);
      ro?.disconnect();
    };
  }, [scheduleScrollStateUpdate]);

  const scrollBy = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const step = Math.max(220, Math.floor(el.clientWidth * 0.72));
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  const arrowBtnClass =
    'absolute top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-md transition hover:border-purple-300 hover:bg-white hover:text-purple-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400';

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => scrollBy(-1)}
        className={`${arrowBtnClass} left-0 ${canScrollLeft ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        aria-label="Scroll left"
        aria-hidden={!canScrollLeft}
        tabIndex={canScrollLeft ? 0 : -1}
      >
        <ChevronLeft className="h-5 w-5" strokeWidth={2.25} aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => scrollBy(1)}
        className={`${arrowBtnClass} right-0 ${canScrollRight ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        aria-label="Scroll right"
        aria-hidden={!canScrollRight}
        tabIndex={canScrollRight ? 0 : -1}
      >
        <ChevronRight className="h-5 w-5" strokeWidth={2.25} aria-hidden />
      </button>
      <div
        ref={scrollRef}
        className={[scrollClassName, 'px-10'].filter(Boolean).join(' ')}
      >
        {children}
      </div>
    </div>
  );
}

function SelectionRemoveButton({ onRemove, opt, attrLabel, className = '' }) {
  if (typeof onRemove !== 'function') return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onRemove();
      }}
      className={[
        'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300',
        className,
      ].join(' ')}
      aria-label={`Remove ${opt} from ${attrLabel}`}
      title="Remove"
    >
      <X className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
    </button>
  );
}

/** Full-screen customise: all selected options — grid or fixed side panel with vertical scroll. */
function FullscreenSelectionsPreviewPanel({
  items,
  sidePanel = false,
  activeAttrKey = null,
  onRowClick = null,
  onRemoveItem = null,
}) {
  const header = (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-2.5">
      <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-700">All selected</div>
      {items.length > 0 ? (
        <div className="rounded-full bg-purple-100 px-2 py-0.5 text-[11px] font-bold text-purple-800">
          {items.length}
        </div>
      ) : null}
    </div>
  );

  if (sidePanel) {
    return (
      <aside
        className="flex min-h-0 w-[min(20rem,34vw)] min-w-[15rem] shrink-0 flex-col border-l border-slate-200 bg-slate-50 shadow-[inset_1px_0_0_rgba(255,255,255,0.6)]"
        aria-label="All selected customisations"
      >
        {header}
        {items.length === 0 ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-3 py-6 text-center">
            <Layers className="h-8 w-8 shrink-0 text-purple-300" strokeWidth={1.35} aria-hidden />
            <p className="text-xs font-medium leading-snug text-slate-600">
              Selections from every tab appear here as you choose them.
            </p>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-2 py-2 [scrollbar-width:thin]">
            <ul className="flex flex-col gap-2 pb-2">
              {items.map((it) => {
                const isActive = activeAttrKey != null && it.attrKey === activeAttrKey;
                const RowInner = (
                  <>
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-slate-50 ring-1 ring-slate-100">
                      <img
                        src={it.thumbSrc}
                        alt={it.opt}
                        className="max-h-12 max-w-12 object-contain"
                        loading="lazy"
                      />
                    </div>
                    <div className="min-w-0 flex-1 py-0.5">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-purple-600">{it.attrLabel}</div>
                      <div className="mt-0.5 text-sm font-semibold leading-snug text-slate-900">{it.opt}</div>
                    </div>
                  </>
                );
                const rowClass = [
                  'flex w-full gap-2.5 rounded-xl border p-2 pr-9 text-left shadow-sm ring-1 transition-colors',
                  isActive
                    ? 'border-purple-400 bg-purple-50/90 ring-purple-200'
                    : 'border-slate-200 bg-white ring-slate-100/80 hover:border-purple-200 hover:bg-purple-50/50',
                ].join(' ');
                return (
                  <li key={it.id} className="relative">
                    {typeof onRowClick === 'function' && it.attrKey != null ? (
                      <button
                        type="button"
                        onClick={() => onRowClick(it.attrKey)}
                        aria-pressed={isActive}
                        title={`Edit ${it.attrLabel}`}
                        className={rowClass}
                      >
                        {RowInner}
                      </button>
                    ) : (
                      <div className={rowClass.replace(' text-left', '')}>{RowInner}</div>
                    )}
                    <SelectionRemoveButton
                      onRemove={
                        typeof onRemoveItem === 'function' && it.attrKey != null
                          ? () => onRemoveItem(it.attrKey, it.opt, it)
                          : null
                      }
                      opt={it.opt}
                      attrLabel={it.attrLabel}
                      className="absolute right-2 top-2 z-10"
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </aside>
    );
  }

  return (
    <div className="mt-1 flex min-h-0 min-w-0 flex-1 flex-col rounded-2xl border border-slate-200 bg-white p-4">
      <div className="mb-2">{header}</div>
      {items.length === 0 ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/90 px-4 py-8">
          <Layers className="h-9 w-9 shrink-0 text-purple-300" strokeWidth={1.35} aria-hidden />
          <p className="max-w-[22rem] text-center text-sm font-medium leading-snug text-slate-600">
            When you pick options on any tab, they will all be listed here with icons
          </p>
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-2 gap-3 overflow-y-auto overscroll-y-contain pb-1 [scrollbar-width:thin] sm:grid-cols-3 lg:grid-cols-4 auto-rows-min">
          {items.map((it) => {
            const isActive = activeAttrKey != null && it.attrKey === activeAttrKey;
            const cardClass = [
              'flex min-h-0 flex-col rounded-xl border p-2.5 shadow-sm',
              isActive ? 'border-purple-400 bg-purple-50/90 ring-2 ring-purple-200' : 'border-purple-100 bg-gradient-to-b from-white to-slate-50/90',
            ].join(' ');
            const inner = (
              <>
                <div className="flex min-h-[5.5rem] flex-1 items-center justify-center rounded-lg bg-white p-2 ring-1 ring-slate-100">
                  <img
                    src={it.thumbSrc}
                    alt={it.opt}
                    className="max-h-[100px] w-full max-w-[100px] object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="mt-2 min-w-0 text-[10px] font-semibold uppercase tracking-wide text-purple-500 line-clamp-2">
                  {it.attrLabel}
                </div>
                <div className="mt-0.5 line-clamp-3 text-xs font-semibold leading-snug text-slate-900">{it.opt}</div>
              </>
            );
            const cardBody =
              typeof onRowClick === 'function' && it.attrKey != null ? (
                <button
                  type="button"
                  onClick={() => onRowClick(it.attrKey)}
                  aria-pressed={isActive}
                  title={`Edit ${it.attrLabel}`}
                  className={`${cardClass} w-full text-left transition-colors hover:border-purple-300`}
                >
                  {inner}
                </button>
              ) : (
                <div className={cardClass}>{inner}</div>
              );
            return (
              <div key={it.id} className="relative">
                {cardBody}
                <SelectionRemoveButton
                  onRemove={
                    typeof onRemoveItem === 'function' && it.attrKey != null
                      ? () => onRemoveItem(it.attrKey, it.opt, it)
                      : null
                  }
                  opt={it.opt}
                  attrLabel={it.attrLabel}
                  className="absolute right-1.5 top-1.5 z-10"
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Below md: horizontal scroll of all selections under the main preview (inline + mobile modal). */
function MobileAllSelectionsHorizontalStrip({ items, activeAttrKey, onChipClick, onRemoveItem = null }) {
  if (!items?.length) return null;
  return (
    <div
      className="md:hidden mt-2 shrink-0 border-t border-slate-100 pt-2"
      aria-label="All selected customisations"
    >
      <div className="mb-1.5 px-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600">
        All selected <span className="font-semibold text-purple-700">({items.length})</span>
      </div>
      <div className="-mx-0.5 flex gap-2 overflow-x-auto overscroll-x-contain px-0.5 pb-1 touch-pan-x [-webkit-overflow-scrolling:touch] [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300">
        {items.map((it) => {
          const isActive = activeAttrKey != null && it.attrKey === activeAttrKey;
          return (
            <div key={it.id} className="relative w-[5.25rem] shrink-0">
              <button
                type="button"
                onClick={() => onChipClick(it.attrKey)}
                aria-pressed={isActive}
                title={`${it.attrLabel}: ${it.opt}`}
                className={[
                  'flex w-full flex-col items-center rounded-xl border px-1.5 py-2 pt-3 text-center shadow-sm transition-colors',
                  isActive
                    ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-200'
                    : 'border-slate-200 bg-white hover:border-purple-200 hover:bg-slate-50',
                ].join(' ')}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-50 ring-1 ring-slate-100">
                  <img src={it.thumbSrc} alt={it.opt} className="max-h-11 max-w-11 object-contain" loading="lazy" />
                </div>
                <div className="mt-1.5 line-clamp-2 w-full text-[9px] font-bold uppercase leading-tight text-purple-600">
                  {it.attrLabel}
                </div>
                <div className="mt-0.5 line-clamp-2 w-full text-[10px] font-semibold leading-tight text-slate-900">{it.opt}</div>
              </button>
              <SelectionRemoveButton
                onRemove={
                  typeof onRemoveItem === 'function' && it.attrKey != null
                    ? () => onRemoveItem(it.attrKey, it.opt, it)
                    : null
                }
                opt={it.opt}
                attrLabel={it.attrLabel}
                className="absolute -right-0.5 -top-0.5 z-10 !h-5 !w-5"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Map key for customisation state when the same type value appears under multiple categories (e.g. men vs women kurta). */
function compositeCustomizationKey(categoryValue, typeValue) {
  const c = categoryValue != null && String(categoryValue) !== '' ? String(categoryValue) : '_';
  const v = typeValue != null && String(typeValue) !== '' ? String(typeValue) : '_';
  return `${c}::${v}`;
}

const MENS_REFERENCE_IMAGE_TYPES = new Set([
  'tshirt',
  'kurta',
  'kurta-dhoti',
  'pajama',
  'jacket',
  'shirt',
  'trouser',
  'blazer',
  'waistcoat',
  'cordset',
  'socks',
]);

const WOMENS_REFERENCE_IMAGE_TYPES = new Set([
  'top',
  'dress',
  'jeans',
  'skirt',
  'trouser',
  'lehenga',
  'blouses',
  'saree-styles',
  'night-wear',
  'lingerie',
  'blazer',
  'hoodies',
  'layering',
  'jacket',
  'shirt',
  'socks',
]);

/** Whether a product type uses image assets (not icon SVGs) for customization references. */
function isReferenceImageProductType(categoryValue, typeValue, kidAgeRange = null) {
  if (!typeValue) return false;
  if (typeValue === 'socks') return true;
  if (isMensCatalogContext(categoryValue, kidAgeRange) && MENS_REFERENCE_IMAGE_TYPES.has(typeValue)) {
    return true;
  }
  if (
    (categoryValue === 'kids-boy' || categoryValue === 'kids-girl') &&
    kidAgeRange === 'toddlers' &&
    typeValue === 'toddler-dress'
  ) {
    return true;
  }
  if (isWomensCatalogContext(categoryValue, kidAgeRange)) {
    if (WOMENS_REFERENCE_IMAGE_TYPES.has(typeValue)) return true;
    if (isWomenShirtType(typeValue)) return true;
    return false;
  }
  return false;
}

/** True when generation ref fields on selected types need syncing to parent state. */
function typesNeedGenerationRefSync(prevTypes, nextTypes) {
  const prev = prevTypes || [];
  const next = nextTypes || [];
  if (prev.length !== next.length) return true;
  for (let i = 0; i < prev.length; i += 1) {
    const a = prev[i];
    const b = next[i];
    if (!a || !b) return true;
    if (a.value !== b.value || a.category !== b.category || Boolean(a.parentType) !== Boolean(b.parentType)) {
      return true;
    }
    if ((a.garmentInspirationImage ?? null) !== (b.garmentInspirationImage ?? null)) return true;
    if (JSON.stringify(a.customizationReferences ?? null) !== JSON.stringify(b.customizationReferences ?? null)) {
      return true;
    }
  }
  return false;
}

const MultiProductTypeSelector = ({
  selectedCategory,
  selectedTypes = [],
  onTypesChange,
  onActiveCategoryChange = null,
  kidAgeRange = null,
  showTip,
  onTipToggle,
  className = '',
  maxSelections = 5,
  inline = false,
  /** Selected inspiration or fabric-template design to show in the desktop preview pane */
  selectedDesignPreview = null,
  /** Clears the selected design; when provided, a cross button is shown on the preview image */
  onClearSelectedDesign = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTypeValue, setActiveTypeValue] = useState(null);
  const [inlineCustomizeOpen, setInlineCustomizeOpen] = useState(false);
  const [desktopCustomizeFullscreen, setDesktopCustomizeFullscreen] = useState(false);
  const [mobileCustomizeModalOpen, setMobileCustomizeModalOpen] = useState(false);
  const [activeCustomizationKey, setActiveCustomizationKey] = useState(null);
  const [customizationByType, setCustomizationByType] = useState({});
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customTypeText, setCustomTypeText] = useState('');
  const containerRef = useRef(null);
  const customInputRef = useRef(null);
  const customizeSessionSnapshotRef = useRef(null);
  const onTypesChangeRef = useRef(onTypesChange);
  const userReferenceInputRef = useRef(null);

  useEffect(() => {
    onTypesChangeRef.current = onTypesChange;
  }, [onTypesChange]);
  const [customizationUserReferencesByType, setCustomizationUserReferencesByType] = useState({});
  const [userReferenceUploadingKey, setUserReferenceUploadingKey] = useState(null);

  const selectedBaseTypesAll = useMemo(() => {
    return (selectedTypes || []).filter((t) => t && t.value && !t.parentType);
  }, [selectedTypes]);

  const activeTypeMeta = useMemo(() => {
    if (!activeTypeValue) return null;
    const cat = selectedCategory?.value;
    if (cat) {
      return (
        (selectedTypes || []).find((t) => t && !t.parentType && t.value === activeTypeValue && t.category === cat) ||
        null
      );
    }
    return (selectedTypes || []).find((t) => t && !t.parentType && t.value === activeTypeValue) || null;
  }, [selectedTypes, activeTypeValue, selectedCategory?.value]);

  const activeTypeCategoryValue = activeTypeMeta?.category || selectedCategory?.value || null;

  /** categoryHint: grid clicks pass selectedCategory so we never resolve another category's duplicate type value first. */
  const setActiveTypeAndSyncCategory = (typeValue, categoryHint = null) => {
    setActiveTypeValue(typeValue);
    const meta =
      categoryHint != null && categoryHint !== ''
        ? (selectedTypes || []).find(
            (t) => t && !t.parentType && t.value === typeValue && t.category === categoryHint
          ) || null
        : (selectedTypes || []).find((t) => t && !t.parentType && t.value === typeValue) || null;
    const desiredCategory = meta?.category || null;
    if (desiredCategory && selectedCategory?.value && desiredCategory !== selectedCategory.value && typeof onActiveCategoryChange === 'function') {
      onActiveCategoryChange(desiredCategory);
    }
  };

  const closeModal = () => {
    setIsOpen(false);
    setShowCustomInput(false);
    setCustomTypeText('');
  };

  useEffect(() => {
    if (inline) return;
    if (isOpen) return;
  }, [isOpen, inline]);

  useEffect(() => {
    if (inline) return undefined;
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, inline]);

  useEffect(() => {
    if (inline) return undefined;
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (showCustomInput) {
        setShowCustomInput(false);
        setCustomTypeText('');
        e.preventDefault();
        return;
      }
      closeModal();
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [isOpen, showCustomInput, inline]);

  useEffect(() => {
    if (inline) return;
    if (!isOpen) return;
    const t = window.setTimeout(() => {
      if (showCustomInput) {
        customInputRef.current?.focus();
      }
    }, 50);
    return () => window.clearTimeout(t);
  }, [isOpen, showCustomInput, inline]);

  const selectedBaseTypesSignature = useMemo(
    () =>
      selectedBaseTypesAll
        .map((t) => `${t.category ?? ''}:${t.value}`)
        .join('|'),
    [selectedBaseTypesAll]
  );

  const selectedTypesCustomizationHydrationSignature = useMemo(
    () =>
      JSON.stringify(
        (selectedTypes || [])
          .filter((t) => t && t.value && !t.parentType)
          .map((t) => ({
            k: compositeCustomizationKey(t.category, t.value),
            c: t.customization ?? null,
            u: t.customizationUserReferences ?? null,
          }))
      ),
    [selectedTypes]
  );

  useEffect(() => {
    const effectiveOpen = inline ? true : isOpen;
    if (!effectiveOpen) return;

    const currentCategory = selectedCategory?.value;

    setActiveTypeValue((prev) => {
      if (selectedBaseTypesAll.length === 0) {
        return prev === null ? prev : null;
      }

      const stillValid =
        prev &&
        selectedBaseTypesAll.some(
          (t) =>
            t.value === prev &&
            (!currentCategory || !t.category || t.category === currentCategory)
        );
      if (stillValid) return prev;

      const fallback =
        (currentCategory ? selectedBaseTypesAll.find((t) => t.category === currentCategory) : null) ||
        selectedBaseTypesAll[0] ||
        null;
      return fallback?.value ?? null;
    });
  }, [isOpen, inline, selectedCategory?.value, selectedBaseTypesSignature]);

  useEffect(() => {
    if (!inline) return;
    if (inlineCustomizeOpen && selectedBaseTypesAll.length === 0) {
      setInlineCustomizeOpen(false);
      setActiveTypeValue(null);
    }
  }, [inline, inlineCustomizeOpen, selectedBaseTypesAll.length]);

  // Hydrate per-type customisation state from `selectedTypes` so inline/mobile stay consistent.
  useEffect(() => {
    const effectiveOpen = inline ? true : isOpen;
    if (!effectiveOpen) return;
    setCustomizationByType((prev) => {
      const next = {};
      for (const t of selectedTypes || []) {
        if (!t || !t.value) continue;
        if (t.parentType) continue; // only top-level types
        if (t.customization && typeof t.customization === 'object') {
          next[compositeCustomizationKey(t.category, t.value)] = t.customization;
        }
      }
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(next || {});
      if (prevKeys.length === nextKeys.length && prevKeys.every((k) => JSON.stringify(prev[k] || {}) === JSON.stringify(next[k] || {}))) {
        return prev;
      }
      return next;
    });
  }, [selectedTypesCustomizationHydrationSignature, isOpen, inline]);

  useEffect(() => {
    const effectiveOpen = inline ? true : isOpen;
    if (!effectiveOpen) return;
    setCustomizationUserReferencesByType((prev) => {
      const next = {};
      for (const t of selectedTypes || []) {
        if (!t || !t.value || t.parentType) continue;
        const refs = t.customizationUserReferences;
        if (refs && typeof refs === 'object' && !Array.isArray(refs)) {
          next[compositeCustomizationKey(t.category, t.value)] = refs;
        }
      }
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(next || {});
      if (
        prevKeys.length === nextKeys.length &&
        prevKeys.every((k) => JSON.stringify(prev[k] || {}) === JSON.stringify(next[k] || {}))
      ) {
        return prev;
      }
      return next;
    });
  }, [selectedTypesCustomizationHydrationSignature, isOpen, inline]);

  const getFilteredTypes = () => {
    if (!selectedCategory?.types) return [];
    if (
      kidAgeRange === 'toddlers' &&
      (selectedCategory?.value === 'kids-boy' || selectedCategory?.value === 'kids-girl')
    ) {
      return [{
        label: 'Toddler Dress',
        value: 'toddler-dress',
        img: selectedCategory?.value === 'kids-girl' ? KIDS_GIRL_TODDLER_PREVIEW : KIDS_BOY_TODDLER_PREVIEW,
      }];
    }
    if (selectedCategory?.value === 'kids-boy' && kidsBoyUsesMensCatalog(kidAgeRange)) {
      const mensCategory = PRODUCT_CATEGORIES_OPTIMIZED.find((c) => c.value === 'men-clothing');
      return mensCategory?.types || selectedCategory.types;
    }
    if (selectedCategory?.value === 'kids-girl' && kidsGirlUsesWomensCatalog(kidAgeRange)) {
      const womenCategory = PRODUCT_CATEGORIES_OPTIMIZED.find((c) => c.value === 'women-clothing');
      return filterKidsGirlProductTypes(womenCategory?.types || selectedCategory.types);
    }
    return selectedCategory.types;
  };

  /** True if this top-level type is already in the selection (flat or legacy subtype). */
  const isTypeOptionSelected = (type) => {
    const currentCategory = selectedCategory?.value;
    return (selectedTypes || []).some((selected) => {
      const categoryMatch = !currentCategory || selected.category === currentCategory;
      if (!categoryMatch) return false;
      if (selected.parentType === type.value) return true;
      if (selected.value === type.value && !selected.parentType) return true;
      if (
        typeof selected.value === 'string' &&
        selected.value !== type.value &&
        (selected.value.startsWith(`${type.value}-`) ||
          selected.value.startsWith(`${String(type.value).replace(/\s+/g, '-')}-`))
      ) {
        return true;
      }
      return false;
    });
  };

  const handleTypeSelect = (type) => {
    if ((selectedTypes || []).length >= maxSelections) return;
    if (isTypeOptionSelected(type)) return;

    const newType = {
      label: type.label,
      value: type.value,
      img: type.img,
      parentType: null,
      parentLabel: null,
      finalValue: type.value,
      finalLabel: type.label,
      category: selectedCategory?.value,
      categoryLabel: selectedCategory?.label,
    };

    const ckAdd = compositeCustomizationKey(selectedCategory?.value, type.value);
    const userRefsAdd = customizationUserReferencesByType[ckAdd] || {};
    const customizationAdd = customizationByType[ckAdd] || {};
    const withRefs = applyGenerationRefsToType(newType, customizationAdd, userRefsAdd);
    onTypesChange([...selectedTypes, withRefs]);
  };

  const unselectTypeByValue = (typeValue, categoryValue = null) => {
    const next = selectedTypes.filter((t) => {
      if (!t || t.parentType) return true;
      if (t.value !== typeValue) return true;
      if (categoryValue && t.category && t.category !== categoryValue) return true;
      return false;
    });
    if (next.length !== selectedTypes.length) {
      onTypesChange(next);
    }

    const ckRm = compositeCustomizationKey(categoryValue ?? '_', typeValue);
    setCustomizationByType((prev) => {
      let changed = false;
      let rest = { ...prev };
      if (rest[typeValue]) {
        delete rest[typeValue];
        changed = true;
      }
      if (rest[ckRm]) {
        delete rest[ckRm];
        changed = true;
      }
      return changed ? rest : prev;
    });

    setActiveTypeValue((prev) => {
      if (prev !== typeValue) return prev;
      const anySameValueLeft = next.some((t) => !t.parentType && t.value === typeValue);
      return anySameValueLeft ? prev : next.find((t) => !t.parentType)?.value ?? null;
    });
  };

  const removeType = (index) => {
    const newTypes = selectedTypes.filter((_, i) => i !== index);
    onTypesChange(newTypes);
  };

  const handleCustomTypeAdd = () => {
    if (!customTypeText.trim() || selectedTypes.length >= maxSelections) {
      return;
    }

    const customType = {
      value: customTypeText.trim(),
      label: customTypeText.trim(),
      parentType: null,
      parentLabel: null,
      finalValue: customTypeText.trim(),
      finalLabel: customTypeText.trim(),
      isCustom: true,
      category: selectedCategory?.value,
      categoryLabel: selectedCategory?.label,
    };

    onTypesChange([...selectedTypes, customType]);
    setCustomTypeText('');
    setShowCustomInput(false);
  };

  const filteredTypes = getFilteredTypes();
  const isReferenceImageTypeActive = isReferenceImageProductType(
    activeTypeCategoryValue,
    activeTypeValue,
    kidAgeRange
  );

  const buildCustomizationReferences = (
    customizationObj,
    typeValue,
    categoryForReferences = null,
    userReferences = null
  ) => {
    const catRef = categoryForReferences ?? selectedCategory?.value;
    const typeUsesReferenceImages = isReferenceImageProductType(catRef, typeValue, kidAgeRange);
    const out = {};
    const customObj = customizationObj && typeof customizationObj === 'object' ? customizationObj : {};
    const userRefsMap = userReferences && typeof userReferences === 'object' ? userReferences : {};

    for (const [attrKey, userRef] of Object.entries(userRefsMap)) {
      if (!userRef?.imageUrl) continue;
      out[attrKey] = [buildUserReferenceOnlyEntry(userRef)];
    }

    for (const [attrKey, opts] of Object.entries(customObj)) {
      if (userRefsMap[attrKey]?.imageUrl) continue;
      if (!Array.isArray(opts) || opts.length === 0) continue;
      out[attrKey] = opts
        .map((opt) =>
          resolveCustomizationReferenceEntry(opt, attrKey, {
            categoryValue: catRef,
            typeValue,
            kidAgeRange,
            isReferenceImageTypeActive: typeUsesReferenceImages,
            userRef: null,
          })
        )
        .filter(Boolean);
    }
    return Object.keys(out).length > 0 ? out : null;
  };

  const applyGenerationRefsToType = (typeObj, customizationObj, userReferences = {}) => {
    const customizationReferences = buildCustomizationReferences(
      customizationObj,
      typeObj.value,
      typeObj.category,
      userReferences
    );
    const hasSelections = hasCustomizationSelections(customizationObj, userReferences);
    const garmentInspirationImage = hasSelections
      ? null
      : buildGarmentInspirationImageUrl(typeObj.category, typeObj.value, kidAgeRange);
    const next = {
      ...typeObj,
      customization: customizationObj,
      customizationReferences,
    };
    if (Object.keys(userReferences).length > 0) {
      next.customizationUserReferences = userReferences;
    } else {
      delete next.customizationUserReferences;
    }
    if (garmentInspirationImage) {
      next.garmentInspirationImage = garmentInspirationImage;
    } else {
      delete next.garmentInspirationImage;
    }
    return next;
  };

  const customizationStateSignature = useMemo(
    () =>
      JSON.stringify({
        byType: customizationByType,
        userRefs: customizationUserReferencesByType,
      }),
    [customizationByType, customizationUserReferencesByType]
  );

  // Keep garment inspiration refs on selected types (shown in left inspiration cards).
  useEffect(() => {
    const effectiveOpen = inline ? true : isOpen;
    if (!effectiveOpen || !onTypesChange || mobileCustomizeModalOpen) return;

    const nextTypes = (selectedTypes || []).map((t) => {
      if (!t || !t.value || t.parentType) return t;
      const ck = compositeCustomizationKey(t.category, t.value);
      const customization =
        customizationByType[ck] || customizationByType[t.value] || t.customization || {};
      const userRefs = customizationUserReferencesByType[ck] || t.customizationUserReferences || {};
      return applyGenerationRefsToType(t, customization, userRefs);
    });

    if (typesNeedGenerationRefSync(selectedTypes, nextTypes)) {
      onTypesChangeRef.current?.(nextTypes);
    }
  }, [
    customizationStateSignature,
    kidAgeRange,
    isOpen,
    inline,
    mobileCustomizeModalOpen,
    selectedBaseTypesSignature,
  ]);

  const syncActiveTypeToSelectedTypes = useCallback((userRefsOverride = null, customizationOverride = null) => {
    const catCtx = activeTypeCategoryValue;
    const typeValue = activeTypeValue;
    if (!typeValue) return;
    const ck = compositeCustomizationKey(catCtx, typeValue);
    const idx = selectedTypes.findIndex(
      (t) =>
        t.value === typeValue &&
        !t.parentType &&
        (!catCtx || !t.category || t.category === catCtx)
    );
    if (idx < 0) return;
    const updated = [...selectedTypes];
    const cur = updated[idx];
    const customization =
      customizationOverride != null
        ? customizationOverride
        : customizationByType[ck] || customizationByType[typeValue] || {};
    const userRefs =
      userRefsOverride != null ? userRefsOverride : customizationUserReferencesByType[ck] || {};
    updated[idx] = applyGenerationRefsToType(cur, customization, userRefs);
    onTypesChange(updated);
  }, [
    activeTypeCategoryValue,
    activeTypeValue,
    selectedTypes,
    customizationByType,
    customizationUserReferencesByType,
    onTypesChange,
    kidAgeRange,
    isReferenceImageTypeActive,
    selectedCategory?.value,
  ]);

  const handleUserReferenceUpload = async (attrKey, event) => {
    const file = event.target.files?.[0];
    if (!file || !activeTypeValue || !attrKey) return;
    if (!file.type.startsWith('image/')) {
      return;
    }
    const catCtx = activeTypeCategoryValue;
    const ck = compositeCustomizationKey(catCtx, activeTypeValue);
    const uploadKey = `${ck}::${attrKey}`;
    setUserReferenceUploadingKey(uploadKey);
    let previewUrl = null;
    try {
      previewUrl = URL.createObjectURL(file);
      const compressed = await compressImage(file, { maxWidth: 1600, maxHeight: 1600, quality: 0.88 });
      const uploadResult = await uploadImageWithFallback(compressed, {
        folder: 'customization-references',
      });
      const uploadedUrl = uploadResult?.url || (typeof uploadResult === 'string' ? uploadResult : null);
      if (!uploadedUrl) {
        throw new Error('Upload did not return a URL');
      }
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        previewUrl = null;
      }
      const prevRef = customizationUserReferencesByType[ck]?.[attrKey];
      const nextTypeRefs = {
        ...(customizationUserReferencesByType[ck] || {}),
        [attrKey]: {
          imageUrl: uploadedUrl,
          fileName: file.name,
          mimeType: file.type || 'image/jpeg',
          customName: prevRef?.customName || '',
        },
      };
      const clearedCustomization = {
        ...(customizationByType[ck] || customizationByType[activeTypeValue] || {}),
        [attrKey]: [],
      };
      setCustomizationByType((prev) => ({
        ...prev,
        [ck]: clearedCustomization,
      }));
      setCustomizationUserReferencesByType((prev) => ({
        ...prev,
        [ck]: nextTypeRefs,
      }));
      syncActiveTypeToSelectedTypes(nextTypeRefs, clearedCustomization);
    } catch (err) {
      console.error('Customization reference upload failed:', err);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    } finally {
      setUserReferenceUploadingKey(null);
      if (userReferenceInputRef.current) {
        userReferenceInputRef.current.value = '';
      }
    }
  };

  const handleRemoveUserReference = (attrKey) => {
    if (!activeTypeValue || !attrKey) return;
    const catCtx = activeTypeCategoryValue;
    const ck = compositeCustomizationKey(catCtx, activeTypeValue);
    const nextTypeRefs = { ...(customizationUserReferencesByType[ck] || {}) };
    delete nextTypeRefs[attrKey];
    setCustomizationUserReferencesByType((prev) => {
      const next = { ...prev };
      if (Object.keys(nextTypeRefs).length === 0) {
        delete next[ck];
        return next;
      }
      next[ck] = nextTypeRefs;
      return next;
    });
    syncActiveTypeToSelectedTypes(nextTypeRefs);
  };

  const handleUserReferenceCustomNameChange = (attrKey, customName) => {
    if (!activeTypeValue || !attrKey) return;
    const catCtx = activeTypeCategoryValue;
    const ck = compositeCustomizationKey(catCtx, activeTypeValue);
    const existing = customizationUserReferencesByType[ck]?.[attrKey];
    if (!existing?.imageUrl) return;
    const nextTypeRefs = {
      ...(customizationUserReferencesByType[ck] || {}),
      [attrKey]: { ...existing, customName },
    };
    setCustomizationUserReferencesByType((prev) => ({
      ...prev,
      [ck]: nextTypeRefs,
    }));
    syncActiveTypeToSelectedTypes(nextTypeRefs);
  };

  const handleRemoveCustomizationItem = (attrKey, opt, item) => {
    if (item?.isUserReference) {
      handleRemoveUserReference(attrKey);
      return;
    }
    if (!activeTypeValue || !attrKey || !opt) return;
    toggleCustomization(activeTypeValue, attrKey, opt);
  };

  const toggleCustomization = (typeValue, groupKey, optionLabel) => {
    const catCtx = activeTypeCategoryValue || selectedCategory?.value || null;
    const ck = compositeCustomizationKey(catCtx, typeValue);
    const prevType = customizationByType[ck] || customizationByType[typeValue] || {};
    const curArrBefore = prevType[groupKey] || [];
    const isSelecting = !curArrBefore.includes(optionLabel);

    let userRefs = { ...(customizationUserReferencesByType[ck] || {}) };
    if (isSelecting && userRefs[groupKey]) {
      delete userRefs[groupKey];
      setCustomizationUserReferencesByType((prev) => {
        const next = { ...prev };
        const typeRefs = { ...(next[ck] || {}) };
        delete typeRefs[groupKey];
        if (Object.keys(typeRefs).length === 0) {
          delete next[ck];
          return next;
        }
        next[ck] = typeRefs;
        return next;
      });
    }

    const waistcoatExclusive =
      isMensCatalogContext(catCtx, kidAgeRange) &&
      typeValue === 'waistcoat' &&
      Object.prototype.hasOwnProperty.call(MENS_WAISTCOAT_ASSET_MAP, groupKey);

    setCustomizationByType((prev) => {
      const prevType = prev[ck] || prev[typeValue] || {};
      const cur = prevType[groupKey] || [];
      // Single-select per row: selecting replaces prior value; tapping again clears.
      const next = cur.includes(optionLabel) ? [] : [optionLabel];
      let nextForType = { ...prevType, [groupKey]: next };
      // Waistcoat: only one choice across Traditional / Nehru / Western tabs.
      if (waistcoatExclusive && next.length > 0) {
        Object.keys(MENS_WAISTCOAT_ASSET_MAP).forEach((k) => {
          if (k !== groupKey) nextForType[k] = [];
        });
      }
      const rest = { ...prev };
      delete rest[typeValue];
      return { ...rest, [ck]: nextForType };
    });

    const idx = selectedTypes.findIndex(
      (t) =>
        t.value === typeValue &&
        !t.parentType &&
        (!catCtx || !t.category || t.category === catCtx)
    );
    if (idx >= 0) {
      const updated = [...selectedTypes];
      const cur = updated[idx];
      const curArr = cur.customization?.[groupKey] || [];
      const nextArr = curArr.includes(optionLabel) ? [] : [optionLabel];
      let nextCustomization = { ...(cur.customization || {}), [groupKey]: nextArr };
      if (waistcoatExclusive && nextArr.length > 0) {
        Object.keys(MENS_WAISTCOAT_ASSET_MAP).forEach((k) => {
          if (k !== groupKey) nextCustomization[k] = [];
        });
      }
      updated[idx] = applyGenerationRefsToType(cur, nextCustomization, userRefs);
      onTypesChange(updated);
    }
  };

  const captureCustomizeSnapshot = useCallback(() => {
    customizeSessionSnapshotRef.current = {
      customizationByType: JSON.parse(JSON.stringify(customizationByType)),
      customizationUserReferencesByType: JSON.parse(
        JSON.stringify(customizationUserReferencesByType)
      ),
      selectedTypesCustomization: Object.fromEntries(
        (selectedTypes || [])
          .filter((t) => t && t.value && !t.parentType)
          .map((t) => {
            const ck = compositeCustomizationKey(t.category, t.value);
            const custom =
              customizationByType[ck] ||
              customizationByType[t.value] ||
              t.customization ||
              {};
            return [ck, JSON.parse(JSON.stringify(custom))];
          })
      ),
    };
  }, [customizationByType, customizationUserReferencesByType, selectedTypes]);

  const restoreCustomizeSnapshot = useCallback(() => {
    const snapshot = customizeSessionSnapshotRef.current;
    if (!snapshot) return;
    setCustomizationByType(JSON.parse(JSON.stringify(snapshot.customizationByType || {})));
    setCustomizationUserReferencesByType(
      JSON.parse(JSON.stringify(snapshot.customizationUserReferencesByType || {}))
    );
    const restoredTypes = (selectedTypes || []).map((t) => {
      if (!t || !t.value || t.parentType) return t;
      const ck = compositeCustomizationKey(t.category, t.value);
      const snapCustom = snapshot.selectedTypesCustomization?.[ck] ?? {};
      const snapUserRefs = snapshot.customizationUserReferencesByType?.[ck] ?? {};
      return applyGenerationRefsToType(
        t,
        JSON.parse(JSON.stringify(snapCustom)),
        JSON.parse(JSON.stringify(snapUserRefs))
      );
    });
    onTypesChange(restoredTypes);
  }, [selectedTypes, onTypesChange]);

  const finishCustomizeSession = useCallback(() => {
    customizeSessionSnapshotRef.current = null;
    setDesktopCustomizeFullscreen(false);
    setMobileCustomizeModalOpen(false);
    setInlineCustomizeOpen(false);
  }, []);

  const cancelCustomizeSession = useCallback(() => {
    restoreCustomizeSnapshot();
    customizeSessionSnapshotRef.current = null;
    setDesktopCustomizeFullscreen(false);
    setMobileCustomizeModalOpen(false);
    setInlineCustomizeOpen(false);
  }, [restoreCustomizeSnapshot]);

  const openDesktopCustomizeFullscreen = useCallback(() => {
    captureCustomizeSnapshot();
    setDesktopCustomizeFullscreen(true);
  }, [captureCustomizeSnapshot]);

  const openInlineCustomizeForType = useCallback(
    (typeValue, categoryValue) => {
      setActiveTypeAndSyncCategory(typeValue, categoryValue);
      setInlineCustomizeOpen(true);
      if (typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches) {
        openDesktopCustomizeFullscreen();
      }
    },
    [openDesktopCustomizeFullscreen]
  );

  const openMobileCustomizeForType = useCallback(
    (typeValue, categoryValue) => {
      setActiveTypeAndSyncCategory(typeValue, categoryValue);
      const schema = getCustomizationForType(typeValue, categoryValue, kidAgeRange);
      if (!schema) return;
      captureCustomizeSnapshot();
      setMobileCustomizeModalOpen(true);
    },
    [kidAgeRange, captureCustomizeSnapshot]
  );

  const renderSelectedInspirationCards = (options = {}) => {
    const { mobile = false } = options;
    if (selectedBaseTypesAll.length === 0) return null;

    return (
      <div className={mobile ? 'grid grid-cols-1 gap-3' : 'grid grid-cols-1 gap-3 sm:grid-cols-2'}>
        {selectedBaseTypesAll.map((t, idx) => {
          const typeLabel = t.finalLabel || t.label || t.value;
          const ckList = compositeCustomizationKey(t.category, t.value);
          const perType =
            customizationByType[ckList] || customizationByType[t.value] || t.customization || {};
          const userRefs =
            customizationUserReferencesByType[ckList] || t.customizationUserReferences || {};
          const schema = getCustomizationForType(t.value, t.category, kidAgeRange);
          const totalSelected = (schema || []).reduce((n, attr) => {
            if (userRefs[attr.key]?.imageUrl) return n + 1;
            const arr = perType[attr.key];
            return n + (Array.isArray(arr) ? arr.length : 0);
          }, 0);
          // Fallback when schema missing but customization object still has selections
          const fallbackSelected =
            totalSelected ||
            Object.values(perType).flat().length +
              Object.values(userRefs).filter((r) => r?.imageUrl).length;
          const isActive =
            activeTypeValue === t.value && (activeTypeCategoryValue ?? null) === (t.category ?? null);
          const typeUsesRefs = isReferenceImageProductType(t.category, t.value, kidAgeRange);
          const customizationPreviewItems =
            fallbackSelected > 0
              ? buildFullscreenCustomizationPreviewItems(schema, perType, {
                  isReferenceImageTypeActive: typeUsesRefs,
                  activeTypeCategoryValue: t.category,
                  activeTypeValue: t.value,
                  kidAgeRange,
                  userReferences: userRefs,
                })
              : [];

          return (
            <GarmentInspirationCard
              key={`insp-${t.category || 'cat'}-${t.value}-${idx}`}
              categoryValue={t.category}
              typeValue={t.value}
              label={typeLabel}
              customizationCount={fallbackSelected}
              customizationPreviewItems={customizationPreviewItems}
              isActive={isActive}
              kidAgeRange={kidAgeRange}
              onSelect={() => setActiveTypeAndSyncCategory(t.value, t.category)}
              onRemove={() => unselectTypeByValue(t.value, t.category || null)}
              onCustomise={
                schema
                  ? () =>
                      mobile
                        ? openMobileCustomizeForType(t.value, t.category)
                        : openInlineCustomizeForType(t.value, t.category)
                  : undefined
              }
            />
          );
        })}
      </div>
    );
  };

  const renderCustomizeSessionFooter = () => (
    <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-3 pb-safe-overlay sm:px-5 sm:py-4 sm:pb-4">
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
        <button
          type="button"
          onClick={cancelCustomizeSession}
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 sm:w-auto"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={finishCustomizeSession}
          className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-fuchsia-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:from-purple-700 hover:to-fuchsia-700 sm:w-auto"
        >
          <Check size={16} />
          Finish customisation
        </button>
      </div>
    </div>
  );

  const renderCustomizeTypeTabs = () => {
    if (selectedBaseTypesAll.length <= 1) return null;
    return (
      <div className="hidden shrink-0 border-b border-slate-100 bg-white px-2 py-2 sm:px-3 md:block">
        <div className="flex gap-2 overflow-x-auto [-webkit-overflow-scrolling:touch] [scrollbar-width:thin] pb-1">
          {selectedBaseTypesAll.map((t) => {
            const ckTab = compositeCustomizationKey(t.category, t.value);
            const n = Object.values(customizationByType[ckTab] || customizationByType[t.value] || t.customization || {}).flat().length;
            const isActive =
              activeTypeValue === t.value && (activeTypeCategoryValue ?? null) === (t.category ?? null);
            const label = formatCustomizeTabLabel(t);
            return (
              <div
                key={`tab-${t.category || 'cat'}-${t.value}`}
                className={[
                  'inline-flex shrink-0 items-center overflow-hidden rounded-full border text-xs font-semibold transition',
                  isActive
                    ? 'border-purple-500 bg-purple-50 text-purple-800'
                    : 'border-slate-200 bg-white text-slate-700',
                ].join(' ')}
              >
                <button
                  type="button"
                  onClick={() => setActiveTypeAndSyncCategory(t.value, t.category)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 hover:bg-purple-50/80"
                  title={customizeTabTitle(t)}
                >
                  <span className="truncate max-w-[10rem]">{label}</span>
                  {n > 0 && (
                    <span className="text-[11px] font-semibold text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">
                      {n}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className="inline-flex h-full items-center justify-center border-l border-slate-200/80 px-2 py-1.5 text-slate-500 hover:bg-purple-100 hover:text-purple-700"
                  title="Remove"
                  aria-label={`Remove ${customizeTabTitle(t)}`}
                  onClick={() => unselectTypeByValue(t.value, t.category || null)}
                >
                  <X size={12} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getDisplayValue = () => {
    if (selectedTypes.length === 0) {
      return `Select product types (max ${maxSelections})`;
    }

    if (selectedTypes.length === 1) {
      return selectedTypes[0].finalLabel;
    }

    return `${selectedTypes.length} types selected`;
  };

  useEffect(() => {
    const schema = getCustomizationForType(activeTypeValue, activeTypeCategoryValue, kidAgeRange);
    if (!activeTypeValue || !schema || schema.length === 0) {
      setActiveCustomizationKey((prev) => (prev === null ? prev : null));
      return;
    }
    setActiveCustomizationKey((prev) => {
      if (prev && schema.some((attr) => attr.key === prev)) return prev;
      return schema[0]?.key ?? null;
    });
  }, [activeTypeValue, activeTypeCategoryValue, kidAgeRange]);

  const openMobileCustomizeModalIfAvailable = useCallback(() => {
    const schema = getCustomizationForType(activeTypeValue, activeTypeCategoryValue, kidAgeRange);
    if (!activeTypeValue || !schema) return;
    captureCustomizeSnapshot();
    setMobileCustomizeModalOpen(true);
  }, [activeTypeValue, activeTypeCategoryValue, kidAgeRange, captureCustomizeSnapshot]);

  useEffect(() => {
    if (!isOpen) {
      setMobileCustomizeModalOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!mobileCustomizeModalOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileCustomizeModalOpen]);

  useEffect(() => {
    if (!inlineCustomizeOpen) {
      setDesktopCustomizeFullscreen(false);
    }
  }, [inlineCustomizeOpen]);

  useEffect(() => {
    if (!inline || !desktopCustomizeFullscreen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [inline, desktopCustomizeFullscreen]);

  useEffect(() => {
    if (!desktopCustomizeFullscreen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelCustomizeSession();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [desktopCustomizeFullscreen, cancelCustomizeSession]);

  const renderActiveTypeCustomizationCard = (cardOptions = {}) => {
    const schema = getCustomizationForType(activeTypeValue, activeTypeCategoryValue, kidAgeRange);
    const hideInspirationBanner =
      desktopCustomizeFullscreen ||
      cardOptions.fullscreenAllSelectionsPreview ||
      Boolean(cardOptions.showMobileHorizontalAllSelections);

    if (!activeTypeValue || !schema) {
      return (
        <div className="hidden h-full min-h-0 flex-col md:flex">
          {activeTypeValue && !hideInspirationBanner ? (
            <GarmentInspirationCard
              categoryValue={activeTypeCategoryValue}
              typeValue={activeTypeValue}
              label={activeTypeMeta?.finalLabel || activeTypeMeta?.label || activeTypeValue}
              kidAgeRange={kidAgeRange}
              banner
            />
          ) : null}
          <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-slate-500">
            Select a type on the left to customise it.
          </div>
        </div>
      );
    }

    const selectedTypeLabel = activeTypeMeta?.finalLabel || activeTypeMeta?.label || activeTypeValue;
    const activeCk = compositeCustomizationKey(activeTypeCategoryValue, activeTypeValue);
    const perType = customizationByType[activeCk] || customizationByType[activeTypeValue] || {};
    const activeUserReferences = customizationUserReferencesByType[activeCk] || {};
    const previewCtxBase = {
      isReferenceImageTypeActive,
      activeTypeCategoryValue,
      activeTypeValue,
      kidAgeRange,
    };
    const previewCtxWithUserRef = {
      ...previewCtxBase,
      userReferences: activeUserReferences,
    };
    const activeKey =
      activeCustomizationKey && schema.some((attr) => attr.key === activeCustomizationKey)
        ? activeCustomizationKey
        : schema[0]?.key;
    const currentAttr = schema.find((attr) => attr.key === activeKey) || schema[0];
    const currentAttrHasUserRef = Boolean(activeUserReferences[currentAttr?.key]?.imageUrl);
    const customizationChoiceCount = schema.reduce((n, attr) => {
      if (activeUserReferences[attr.key]?.imageUrl) return n + 1;
      const arr = perType[attr.key];
      return n + (Array.isArray(arr) ? arr.length : 0);
    }, 0);

    const { Icon: TypeIcon, bg } = getProductTypeVisual({
      value: activeTypeValue,
      label: selectedTypeLabel,
    });

    return (
      <div className="mx-2 mb-2 mt-2 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-[#fdfaff] sm:mx-4 sm:mb-3 sm:mt-3 md:m-4">
        <div className="shrink-0 border-b border-slate-100 bg-[#fdfaff] px-3 py-3 sm:px-4">
          <div className="flex items-center gap-2">
            <span className={`flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-br ${bg}`}>
              <TypeIcon className="h-3.5 w-3.5 text-white" strokeWidth={2} />
            </span>
            <div className="text-sm font-semibold text-purple-700">
              Customise {selectedTypeLabel}
              {customizationChoiceCount > 0 && (
                <span className="ml-2 inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-[11px] font-semibold text-purple-700">
                  {customizationChoiceCount} selected
                </span>
              )}
            </div>
          </div>
        </div>

        {!hideInspirationBanner ? (
          <GarmentInspirationCard
            categoryValue={activeTypeCategoryValue}
            typeValue={activeTypeValue}
            label={selectedTypeLabel}
            kidAgeRange={kidAgeRange}
            showSectionLabel
            banner
            customizationCount={customizationChoiceCount}
            customizationPreviewItems={
              customizationChoiceCount > 0
                ? buildFullscreenCustomizationPreviewItems(schema, perType, previewCtxWithUserRef)
                : []
            }
          />
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden [scrollbar-width:thin]">
          <div className="border-b border-slate-100 bg-white px-3 pt-2 pb-1 sm:px-4">
            <HorizontalScrollWithArrows
              scrollClassName="flex gap-2 overflow-x-auto [-webkit-overflow-scrolling:touch] [scrollbar-width:thin] pb-1 [&::-webkit-scrollbar]:hidden"
            >
              {schema.map((attr) => {
                const isAttrActive = attr.key === activeKey;
                const hasUserRef = Boolean(activeUserReferences[attr.key]?.imageUrl);
                const count = hasUserRef ? 0 : (perType[attr.key] || []).length;
                return (
                  <button
                    key={attr.key}
                    type="button"
                    onClick={() => setActiveCustomizationKey(attr.key)}
                    className={[
                      'shrink-0 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition',
                      isAttrActive
                        ? 'border-purple-500 bg-purple-50 text-purple-800'
                        : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-purple-300 hover:bg-purple-50',
                    ].join(' ')}
                    title={attr.label}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <span className="truncate max-w-[6.5rem] sm:max-w-[8rem]">{attr.label}</span>
                      {hasUserRef ? (
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500 ring-2 ring-white"
                          title="Custom reference uploaded"
                          aria-hidden
                        />
                      ) : null}
                      {count > 0 ? (
                        <span className="rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700">
                          {count}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </HorizontalScrollWithArrows>
          </div>

          {currentAttr && (
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pt-3 pb-4 sm:px-4 min-w-0">
              <div className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-purple-400">
                {currentAttr.label}
              </div>
              <CustomizationUserReferenceUpload
                attrKey={currentAttr.key}
                attrLabel={currentAttr.label}
                userRef={activeUserReferences[currentAttr.key]}
                isUploading={userReferenceUploadingKey === `${activeCk}::${currentAttr.key}`}
                inputRef={userReferenceInputRef}
                onUpload={(e) => handleUserReferenceUpload(currentAttr.key, e)}
                onRemove={() => handleRemoveUserReference(currentAttr.key)}
                onCustomNameChange={(name) => handleUserReferenceCustomNameChange(currentAttr.key, name)}
              />
              {currentAttrHasUserRef ? (
                <p className="mb-3 rounded-lg border border-purple-100 bg-purple-50/60 px-3 py-2 text-xs text-slate-600">
                  Preset options are hidden while your custom reference is active. Remove the reference to pick a
                  standard option.
                </p>
              ) : (
              <HorizontalScrollWithArrows
                className="pb-1"
                scrollClassName="flex gap-2 overflow-x-auto pb-3 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {currentAttr.opts.map((opt) => {
                  const referenceImgSrc = isReferenceImageTypeActive
                    ? getReferenceAsset({
                        categoryValue: activeTypeCategoryValue,
                        typeValue: activeTypeValue,
                        attrKey: currentAttr.key,
                        opt,
                        kidAgeRange,
                      })
                    : null;
                  const IconComp = ICON_MAP[opt] || FallbackIcon;
                  const active = (perType[currentAttr.key] || []).includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => toggleCustomization(activeTypeValue, currentAttr.key, opt)}
                      className={[
                        'group flex w-[88px] flex-shrink-0 flex-col items-center gap-1.5 rounded-xl border px-1.5 py-2.5 transition-all select-none',
                        active
                          ? 'border-purple-500 bg-purple-100'
                          : 'border-purple-100 bg-white hover:border-purple-300 hover:bg-purple-50',
                      ].join(' ')}
                    >
                      <span className="relative flex h-16 w-16 items-center justify-center">
                        {referenceImgSrc ? (
                          <img
                            key={referenceImgSrc}
                            src={toAbsoluteUrl(referenceImgSrc)}
                            alt={opt}
                            className="h-16 w-16 object-contain"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-slate-400">
                            <IconComp />
                          </span>
                        )}
                      </span>
                      <span
                        className={[
                          'text-[10.5px] leading-tight text-center',
                          active ? 'font-semibold text-purple-700' : 'font-medium text-slate-600',
                        ].join(' ')}
                      >
                        {opt}
                      </span>
                    </button>
                  );
                })}
              </HorizontalScrollWithArrows>
              )}

              {(() => {
                if (cardOptions.fullscreenAllSelectionsPreview) {
                  const previewItems = buildFullscreenCustomizationPreviewItems(schema, perType, previewCtxWithUserRef);
                  const selectedArrFs = perType[currentAttr.key] || [];
                  const selectedOptFs =
                    selectedArrFs.length > 0 ? selectedArrFs[selectedArrFs.length - 1] : null;
                  const {
                    src: previewSrcFs,
                    label: previewLabelFs,
                    isUserReference: previewIsUserRefFs,
                  } = getCustomizationAttrPreviewSrc(currentAttr.key, selectedOptFs, previewCtxWithUserRef);
                  return (
                    <div className="mt-1 flex min-h-0 min-w-0 flex-1 flex-row overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm ring-1 ring-slate-100/80">
                      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-2 sm:p-3">
                        <CustomizationPreviewPanel
                          selectedOpt={previewLabelFs}
                          previewSrc={previewSrcFs}
                          currentAttrLabel={currentAttr.label}
                          primaryInFullscreen
                          primaryBesideSidePanel
                          isUserReference={previewIsUserRefFs}
                        />
                      </div>
                      <FullscreenSelectionsPreviewPanel
                        items={previewItems}
                        sidePanel
                        activeAttrKey={activeKey}
                        onRowClick={setActiveCustomizationKey}
                        onRemoveItem={handleRemoveCustomizationItem}
                      />
                    </div>
                  );
                }

                const selectedArr = perType[currentAttr.key] || [];
                const selectedOpt =
                  selectedArr.length > 0 ? selectedArr[selectedArr.length - 1] : null;
                const {
                  src: previewSrc,
                  label: previewLabel,
                  isUserReference: previewIsUserRef,
                } = getCustomizationAttrPreviewSrc(currentAttr.key, selectedOpt, previewCtxWithUserRef);

                return (
                  <>
                    <CustomizationPreviewPanel
                      selectedOpt={previewLabel}
                      previewSrc={previewSrc}
                      currentAttrLabel={currentAttr.label}
                      isUserReference={previewIsUserRef}
                      compactMobile={Boolean(cardOptions.showMobileHorizontalAllSelections)}
                    />
                    {cardOptions.showMobileHorizontalAllSelections ? (
                      <MobileAllSelectionsHorizontalStrip
                        items={buildFullscreenCustomizationPreviewItems(schema, perType, previewCtxWithUserRef)}
                        activeAttrKey={activeKey}
                        onChipClick={setActiveCustomizationKey}
                        onRemoveItem={handleRemoveCustomizationItem}
                      />
                    ) : null}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    );
  };

  const modalContent =
    !inline && isOpen && selectedCategory && selectedCategory.types ? (
      <div
        className="fixed inset-0 z-[10002] flex h-[100dvh] max-h-[100dvh] w-full flex-col overflow-hidden"
        role="presentation"
      >
        <button
          type="button"
          className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
          aria-label="Close dialog"
          onClick={closeModal}
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="product-type-modal-title"
          aria-hidden={mobileCustomizeModalOpen}
          className={[
            // Always edge-to-edge fullscreen (incl. large phones / small tablets).
            // Avoid sm/md card sizing — those breakpoints fire on big mobile screens.
            'relative z-10 flex h-full min-h-0 w-full max-w-none flex-1 flex-col overflow-hidden rounded-none border-0 bg-white shadow-2xl',
            mobileCustomizeModalOpen ? 'invisible pointer-events-none' : '',
          ].join(' ')}
          style={{ height: '100dvh', maxHeight: '100dvh' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-3 pb-3 pt-safe-overlay sm:px-4 sm:pb-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <Search size={18} className="shrink-0 text-purple-500" aria-hidden />
                <h2 id="product-type-modal-title" className="truncate text-base font-semibold text-slate-900 sm:text-lg">
                  Product types
                </h2>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-600 transition hover:bg-gray-200 active:scale-95"
                title="Close"
                aria-label="Close"
              >
                <X size={22} />
              </button>
            </div>

            <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
              <span>
                {selectedTypes.length} of {maxSelections} selected
              </span>
              <span className="hidden text-gray-400 sm:inline">Tap types to add</span>
            </div>
          </div>

          <div className="shrink-0 border-b-2 border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50 p-3 sm:px-4">
            {!showCustomInput ? (
              <button
                type="button"
                onClick={() => {
                  setShowCustomInput(true);
                  setTimeout(() => customInputRef.current?.focus(), 50);
                }}
                className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-purple-300 bg-white py-2.5 text-sm font-medium text-purple-600 transition-all hover:border-purple-500 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={selectedTypes.length >= maxSelections}
              >
                <Edit3 size={14} />
                <span>+ Custom prompt</span>
              </button>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-semibold text-purple-800">
                  <span className="flex items-center gap-1">
                    <Edit3 size={12} className="text-purple-600" />
                    Custom prompt
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomInput(false);
                      setCustomTypeText('');
                    }}
                    className="rounded p-1 text-gray-500 hover:bg-white/80 hover:text-gray-700"
                  >
                    <X size={14} />
                  </button>
                </div>
                <div className="flex gap-2">
                  <input
                    ref={customInputRef}
                    type="text"
                    placeholder="e.g. Evening gown"
                    value={customTypeText}
                    onChange={(e) => setCustomTypeText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleCustomTypeAdd();
                      }
                    }}
                    className="min-w-0 flex-1 rounded-lg border-2 border-purple-300 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    maxLength={100}
                  />
                  <button
                    type="button"
                    onClick={handleCustomTypeAdd}
                    disabled={!customTypeText.trim()}
                    className="shrink-0 rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
                <p className="text-xs text-gray-600">Enter to add · Esc to cancel</p>
              </div>
            )}
          </div>

          <div className="flex min-h-0 flex-1 flex-col bg-white md:flex-row min-w-0">
            {filteredTypes.length === 0 ? (
              <div className="flex-1 bg-white p-2 sm:p-4">
                <div className="py-10 text-center text-sm text-gray-500">No product types available.</div>
              </div>
            ) : (
              <>
                {/* LEFT: product types */}
                <div className="min-h-0 flex-1 overflow-x-hidden bg-white p-2 sm:p-4 md:w-[42%] md:flex-none md:border-r md:border-slate-100 min-w-0">
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-5 sm:gap-2.5">
                    {filteredTypes.map((type) => {
                      const selected = isTypeOptionSelected(type);
                      const disabled = selectedTypes.length >= maxSelections && !selected;
                      const isActive =
                        activeTypeValue === type.value &&
                        (activeTypeMeta?.category ?? null) === (selectedCategory?.value ?? null);
                      const categoryTile = getCategoryTypeTileVisual(
                        selectedCategory?.value,
                        type.value,
                        kidAgeRange
                      );
                      const { Icon, bg } = getProductTypeVisual(type);
                      const tileBg = categoryTile?.bg || bg;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          disabled={disabled}
                          onClick={() => {
                            // Toggle behavior: tap again to unselect.
                            if (selected) {
                              unselectTypeByValue(type.value, selectedCategory?.value || null);
                              return;
                            }

                            setActiveTypeAndSyncCategory(type.value, selectedCategory?.value || null);

                            // Match HTML behavior: tapping toggles selection, and selects expands the panel.
                            if (!selected && !disabled) {
                              handleTypeSelect(type);
                            }
                          }}
                          title={type.label}
                          className={[
                            'flex min-h-[5.25rem] flex-col items-center rounded-xl border p-2 text-center transition-all duration-200',
                            disabled
                              ? 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-40'
                              : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-sm active:scale-[0.98]',
                            selected ? 'border-purple-400 bg-purple-50/90 ring-2 ring-purple-500' : '',
                            !selected && isActive ? 'border-purple-300 ring-1 ring-purple-200' : '',
                          ].join(' ')}
                        >
                          <span
                            className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${tileBg} shadow-sm sm:h-11 sm:w-11`}
                          >
                            {categoryTile ? (
                              <GarmentTypeTileIcon tile={categoryTile} Icon={Icon} />
                            ) : (
                              <Icon className="h-5 w-5 text-white" strokeWidth={2} />
                            )}
                            {selected && (
                              <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-purple-600 text-white shadow">
                                <Check size={10} strokeWidth={3} />
                              </span>
                            )}
                          </span>
                          <span className="mt-1.5 w-full px-0.5 text-[10px] font-semibold leading-tight text-gray-800 line-clamp-2 sm:text-[11px]">
                            {type.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {(() => {
                    const selectedInCategory = selectedTypes.filter(
                      (t) => !selectedCategory?.value || t.category === selectedCategory.value
                    );
                    if (selectedInCategory.length === 0) return null;

                    return (
                      <div className="mt-3 flex flex-wrap items-center gap-1.5 px-1 text-xs font-medium text-purple-300">
                        {selectedInCategory.map((t, i) => {
                          const ckChip = compositeCustomizationKey(t.category, t.value);
                          const n = Object.values(
                            customizationByType[ckChip] || customizationByType[t.value] || t.customization || {}
                          ).flat().length;
                          const isActive =
                            activeTypeValue === t.value && (activeTypeCategoryValue ?? null) === (t.category ?? null);
                          return (
                            <React.Fragment key={`${t.category || 'c'}-${t.value}-${i}`}>
                              {i > 0 && <span className="text-slate-300">›</span>}
                              <button
                                type="button"
                                onClick={() => {
                                  if (activeTypeValue === t.value && (activeTypeCategoryValue ?? null) === (t.category ?? null)) {
                                    setActiveTypeValue(null);
                                    return;
                                  }
                                  setActiveTypeAndSyncCategory(t.value, t.category);
                                }}
                                className={[
                                  'inline-flex items-center gap-1 rounded-full px-2 py-1 transition-colors',
                                  isActive
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'text-purple-500 hover:bg-purple-50 hover:text-purple-700',
                                ].join(' ')}
                                title="Jump to customisation"
                              >
                                <span className="truncate max-w-[8rem]">{formatCustomizeTabLabel(t)}</span>
                                {n > 0 && <span className="text-[11px] text-purple-400">({n})</span>}
                              </button>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Mobile: inspiration cards with Customise on each image */}
                  {selectedBaseTypesAll.length > 0 ? (
                    <div className="mt-3 px-1 md:hidden">{renderSelectedInspirationCards({ mobile: true })}</div>
                  ) : null}
                </div>

                {/* RIGHT: customisation panel (desktop). On mobile it stacks below. */}
                <div className="hidden min-h-0 flex-1 flex-col bg-white md:flex md:w-[58%] md:flex-none min-w-0 overflow-x-hidden">
                  {renderCustomizeTypeTabs()}
                  {renderActiveTypeCustomizationCard()}
                </div>
              </>
            )}
          </div>

          <div className="shrink-0 border-t border-gray-200 bg-gray-50 px-3 pt-3 pb-safe-overlay sm:flex sm:items-center sm:justify-between sm:gap-3 sm:px-4 sm:pb-4">
            <p className="hidden text-xs text-gray-500 sm:block sm:max-w-[55%]">
              Add up to {maxSelections} types, then tap Done. Change category on the previous screen to mix types.
            </p>
            <button
              type="button"
              onClick={closeModal}
              className="w-full rounded-lg bg-purple-600 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-purple-700 sm:ml-auto sm:w-auto sm:min-w-[7rem] sm:py-2.5"
            >
              Done
            </button>
          </div>
        </div>

        {/* Mobile / tablet customisation modal — always fullscreen below desktop */}
        {mobileCustomizeModalOpen ? (
          <div className="fixed inset-0 z-[10003] flex flex-col lg:hidden" role="presentation">
            <button
              type="button"
              className="absolute inset-0 bg-slate-900/50"
              aria-label="Close customisation dialog"
              onClick={cancelCustomizeSession}
            />
            <div
              role="dialog"
              aria-modal="true"
              className="relative z-10 flex h-[100dvh] max-h-[100dvh] min-h-0 w-full flex-1 flex-col overflow-hidden bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="shrink-0 border-b border-slate-200 bg-slate-50 px-3 pb-3 pt-safe-overlay sm:px-4 sm:pb-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-base font-semibold text-slate-900">
                      Customise {activeTypeMeta?.finalLabel || activeTypeMeta?.label || activeTypeValue || 'type'}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">Tap options to select</div>
                  </div>
                  <button
                    type="button"
                    onClick={cancelCustomizeSession}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-600 transition hover:bg-slate-200 active:scale-95"
                    title="Close"
                    aria-label="Close customisation"
                  >
                    <X size={22} />
                  </button>
                </div>
              </div>

              {renderCustomizeTypeTabs()}
              {/* Allow the customisation card (including Preview) to scroll on mobile. */}
              <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
                {renderActiveTypeCustomizationCard({ showMobileHorizontalAllSelections: true })}
              </div>
              {renderCustomizeSessionFooter()}
            </div>
          </div>
        ) : null}
      </div>
    ) : null;

  const inlineContent =
    inline && selectedCategory && selectedCategory.types ? (
      <div className="w-full overflow-x-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="shrink-0 border-b border-slate-200 bg-slate-50 p-3 sm:p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">Product types</div>
              <div className="mt-1 text-xs text-slate-500">
                {selectedTypes.length} of {maxSelections} selected
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                if (showCustomInput) {
                  setShowCustomInput(false);
                  setCustomTypeText('');
                  return;
                }
                setShowCustomInput(true);
                setTimeout(() => customInputRef.current?.focus(), 50);
              }}
              className="shrink-0 rounded-xl border border-purple-200 bg-white px-3 py-2 text-xs font-semibold text-purple-700 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={selectedTypes.length >= maxSelections && !showCustomInput}
              title="Add custom prompt"
            >
              <span className="inline-flex items-center gap-1">
                <Edit3 size={14} />
                {showCustomInput ? 'Close' : 'Custom prompt'}
              </span>
            </button>
          </div>

          {showCustomInput && (
            <div className="mt-3">
              <div className="flex gap-2">
                <input
                  ref={customInputRef}
                  type="text"
                  placeholder="e.g. Evening gown"
                  value={customTypeText}
                  onChange={(e) => setCustomTypeText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCustomTypeAdd();
                    }
                  }}
                  className="min-w-0 flex-1 rounded-xl border border-purple-200 bg-white px-3 py-2 text-sm focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  maxLength={100}
                />
                <button
                  type="button"
                  onClick={handleCustomTypeAdd}
                  disabled={!customTypeText.trim() || selectedTypes.length >= maxSelections}
                  className="shrink-0 rounded-xl bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Add
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">Enter to add</p>
            </div>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col bg-white lg:flex-row lg:items-start min-w-0 overflow-x-hidden">
          <div className="min-h-0 flex-1 overflow-x-hidden bg-white p-3 lg:w-[42%] lg:flex-none lg:border-r lg:border-slate-100 min-w-0">
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 sm:gap-2.5">
              {filteredTypes.map((type) => {
                const selected = isTypeOptionSelected(type);
                const disabled = selectedTypes.length >= maxSelections && !selected;
                const isActive =
                  activeTypeValue === type.value &&
                  (activeTypeMeta?.category ?? null) === (selectedCategory?.value ?? null);
                const categoryTile = getCategoryTypeTileVisual(
                  selectedCategory?.value,
                  type.value,
                  kidAgeRange
                );
                const { Icon, bg } = getProductTypeVisual(type);
                const tileBg = categoryTile?.bg || bg;
                return (
                  <button
                    key={type.value}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      if (selected) {
                        unselectTypeByValue(type.value, selectedCategory?.value || null);
                        return;
                      }
                      setActiveTypeAndSyncCategory(type.value, selectedCategory?.value || null);
                      if (!selected && !disabled) {
                        handleTypeSelect(type);
                      }
                    }}
                    title={type.label}
                    className={[
                      'flex min-h-[5.25rem] flex-col items-center rounded-xl border p-2 text-center transition-all duration-200',
                      disabled
                        ? 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-40'
                        : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-sm active:scale-[0.98]',
                      selected ? 'border-purple-400 bg-purple-50/90 ring-2 ring-purple-500' : '',
                      !selected && isActive ? 'border-purple-300 ring-1 ring-purple-200' : '',
                    ].join(' ')}
                  >
                    <span className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${tileBg} shadow-sm sm:h-11 sm:w-11`}>
                      {categoryTile ? (
                        <GarmentTypeTileIcon tile={categoryTile} Icon={Icon} />
                      ) : (
                        <Icon className="h-5 w-5 text-white" strokeWidth={2} />
                      )}
                      {selected && (
                        <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-purple-600 text-white shadow">
                          <Check size={10} strokeWidth={3} />
                        </span>
                      )}
                    </span>
                    <span className="mt-1.5 w-full px-0.5 text-[10px] font-semibold leading-tight text-gray-800 line-clamp-2 sm:text-[11px]">
                      {type.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col bg-white lg:w-[58%] lg:flex-none min-w-0 overflow-x-hidden">
            {!inlineCustomizeOpen ? (
              <div className="flex min-h-[240px] flex-1 flex-col overflow-y-auto p-4">
                {selectedBaseTypesAll.length === 0 ? (
                  selectedDesignPreview?.htmlContent || selectedDesignPreview?.imageUrl ? (
                    <div className="flex min-h-[200px] flex-1 flex-col">
                      <div className="relative flex min-h-[280px] flex-1 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                        {selectedDesignPreview.htmlContent ? (
                          <iframe
                            title={selectedDesignPreview.name || 'Selected design'}
                            srcDoc={
                              /<html[\s>]|<!doctype/i.test(selectedDesignPreview.htmlContent)
                                ? selectedDesignPreview.htmlContent
                                : `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>html,body{margin:0;height:100%;background:#f3efe6}img{max-width:100%}</style></head><body>${selectedDesignPreview.htmlContent}</body></html>`
                            }
                            sandbox=""
                            referrerPolicy="no-referrer"
                            className="absolute inset-0 h-full w-full border-0 bg-white"
                          />
                        ) : (
                          <img
                            src={selectedDesignPreview.imageUrl}
                            alt={selectedDesignPreview.name || 'Selected design'}
                            className="max-h-[min(52vh,520px)] w-full object-contain"
                          />
                        )}
                        {onClearSelectedDesign ? (
                          <button
                            type="button"
                            onClick={onClearSelectedDesign}
                            className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-slate-600 shadow-md ring-1 ring-slate-200 transition hover:bg-red-50 hover:text-red-600 active:scale-95"
                            aria-label="Remove selected design"
                          >
                            <X size={16} />
                          </button>
                        ) : null}
                      </div>
                      {!String(selectedDesignPreview.category || '').startsWith('Fabric Template') ? (
                        <div className="mt-3 min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">
                            {selectedDesignPreview.name || 'Selected design'}
                          </p>
                          {selectedDesignPreview.category ? (
                            <p className="mt-0.5 truncate text-xs text-slate-500">
                              {selectedDesignPreview.category}
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="flex min-h-[200px] flex-1 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                      Select a product type on the left, then use Customise on the inspiration card.
                    </div>
                  )
                ) : (
                  renderSelectedInspirationCards()
                )}
              </div>
            ) : (
              <>
                <div className="shrink-0 border-b border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-slate-900">
                        Customise {activeTypeMeta?.finalLabel || activeTypeMeta?.label || activeTypeValue || 'type'}
                      </div>
                      <div className="mt-0.5 text-xs text-slate-500">Deep customisation</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {!desktopCustomizeFullscreen ? (
                        <button
                          type="button"
                          onClick={openDesktopCustomizeFullscreen}
                          className="hidden md:inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          title="Expand customisation to full screen"
                          aria-label="Expand customisation to full screen"
                        >
                          <Maximize2 size={14} />
                          Full screen
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => {
                          setDesktopCustomizeFullscreen(false);
                          setInlineCustomizeOpen(false);
                        }}
                        className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        title="Back"
                      >
                        <span className="inline-flex items-center gap-1">
                          <X size={14} />
                          Close
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                {desktopCustomizeFullscreen ? (
                  <div className="hidden min-h-[200px] flex-1 flex-col items-center justify-center gap-3 p-6 text-center md:flex">
                    <p className="text-sm text-slate-600">
                      Customisation is open in full screen. Use Finish or Cancel in the footer when done.
                    </p>
                    <button
                      type="button"
                      onClick={() => setDesktopCustomizeFullscreen(false)}
                      className="inline-flex items-center gap-2 rounded-xl border border-purple-200 bg-purple-50 px-4 py-2 text-xs font-semibold text-purple-800 hover:bg-purple-100"
                    >
                      <Minimize2 size={14} />
                      Exit full screen
                    </button>
                  </div>
                ) : (
                  <>
                    {renderCustomizeTypeTabs()}
                    <div className="min-h-0 flex-1 overflow-hidden">
                      {renderActiveTypeCustomizationCard()}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    ) : null;

  const desktopCustomizeFullscreenPortal =
    inline &&
    inlineCustomizeOpen &&
    desktopCustomizeFullscreen &&
    typeof document !== 'undefined'
      ? createPortal(
          <div className="fixed inset-0 z-[10040] hidden md:block" role="presentation">
            <button
              type="button"
              className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
              aria-label="Cancel customisation"
              onClick={cancelCustomizeSession}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Customise product type"
              className="absolute inset-0 z-10 m-auto flex h-[100dvh] max-h-[100dvh] w-full max-w-none flex-col overflow-hidden border-0 bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="shrink-0 border-b border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">
                      Customise {activeTypeMeta?.finalLabel || activeTypeMeta?.label || activeTypeValue || 'type'}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">Full screen — use Finish or Cancel below</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setDesktopCustomizeFullscreen(false)}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      title="Exit full screen"
                    >
                      <Minimize2 size={14} />
                      Exit full screen
                    </button>
                  </div>
                </div>
              </div>
              {renderCustomizeTypeTabs()}
              <div className="min-h-0 flex-1 overflow-hidden px-2 pb-0 pt-0 sm:px-3">
                {renderActiveTypeCustomizationCard({ fullscreenAllSelectionsPreview: true })}
              </div>
              {renderCustomizeSessionFooter()}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className={`relative multi-product-selector w-full overflow-x-hidden ${className}`} ref={containerRef}>
      {inline ? (
        inlineContent
      ) : (
        <div className="space-y-2">
          {selectedBaseTypesAll.length === 0 && (selectedDesignPreview?.htmlContent || selectedDesignPreview?.imageUrl) ? (
            <div className="relative flex min-h-[220px] items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
              {selectedDesignPreview.htmlContent ? (
                <iframe
                  title={selectedDesignPreview.name || 'Selected design'}
                  srcDoc={
                    /<html[\s>]|<!doctype/i.test(selectedDesignPreview.htmlContent)
                      ? selectedDesignPreview.htmlContent
                      : `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>html,body{margin:0;height:100%;background:#f3efe6}img{max-width:100%}</style></head><body>${selectedDesignPreview.htmlContent}</body></html>`
                  }
                  sandbox=""
                  referrerPolicy="no-referrer"
                  className="absolute inset-0 h-full w-full border-0 bg-white"
                />
              ) : (
                <img
                  src={selectedDesignPreview.imageUrl}
                  alt={selectedDesignPreview.name || 'Selected design'}
                  className="max-h-[min(45vh,420px)] w-full object-contain"
                />
              )}
              {onClearSelectedDesign ? (
                <button
                  type="button"
                  onClick={onClearSelectedDesign}
                  className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-slate-600 shadow-md ring-1 ring-slate-200 transition hover:bg-red-50 hover:text-red-600 active:scale-95"
                  aria-label="Remove selected design"
                >
                  <X size={16} />
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm shadow-sm transition-all hover:bg-gray-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-400"
              onClick={() => setIsOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={isOpen}
            >
              <span className="truncate font-medium text-gray-700">{getDisplayValue()}</span>
              <ChevronDown size={16} className={`shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
      )}

      {!inline && selectedTypes.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selectedTypes.map((type, index) => {
            const hasMultipleCategories =
              selectedTypes.filter((t) => t.category).length > 0 &&
              new Set(selectedTypes.map((t) => t.category).filter(Boolean)).size > 1;
            const displayLabel =
              hasMultipleCategories && type.categoryLabel
                ? `${type.categoryLabel} — ${type.finalLabel}`
                : type.finalLabel;
            const { Icon, bg } = type.isCustom ? DEFAULT_VISUAL : getProductTypeVisual(type);
            return (
              <div
                key={`${type.value}-${type.category || 'default'}-${index}`}
                className="flex max-w-full items-center gap-1.5 rounded-full border border-purple-200 bg-purple-50 py-0.5 pl-1 pr-1.5 text-xs"
              >
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${bg}`}
                  aria-hidden
                >
                  <Icon className="h-3 w-3 text-white" strokeWidth={2} />
                </span>
                <span className="max-w-[10rem] truncate font-medium text-purple-800 sm:max-w-[14rem]" title={displayLabel}>
                  {displayLabel}
                </span>
                <button
                  type="button"
                  onClick={() => removeType(index)}
                  className="shrink-0 rounded-full p-0.5 transition-colors hover:bg-purple-100"
                  title="Remove type"
                >
                  <X size={10} className="text-purple-600 hover:text-purple-800" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {typeof document !== 'undefined' && modalContent ? createPortal(modalContent, document.body) : null}

      {desktopCustomizeFullscreenPortal}

      {showTip && (
        <div className="absolute right-0 top-12 z-10 max-w-xs rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg animate-in slide-in-from-top-2 duration-200">
          <div className="mb-1 font-medium">Multiple Product Types</div>
          <div className="text-gray-300">Select up to {maxSelections} types. Each selection creates a separate kit.</div>
        </div>
      )}
    </div>
  );
};

export default MultiProductTypeSelector;
